import React, { useState, useEffect, FormEvent, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronDown, ChevronUp, X, ChevronRight, CheckCircle, FileText, Calendar, Users, DollarSign, CreditCard, RefreshCcw, Package2, AlertCircle, Shield, Lock } from 'lucide-react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements, CardElement, AddressElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { useImageViewer } from '../components/ImageViewerProvider';
import { sendProposalLink, setProposalServiceSelection, ProposalService, logProposalEvent, logProposalView } from '../lib/proposals';
import { createProposalViewedMilestone, createProposalApprovedMilestone } from '../lib/bookingMilestones';
import { useSupabase } from '../contexts/SupabaseContext';
import { formatCurrency } from '../lib/utils';
import RichTextDescription from '../components/RichTextDescription';
import { ProductPhotoService } from '../services/productPhotoService';
import toast from 'react-hot-toast';

/** Official multicolor Google "G" — used by the reviews trust badge. */
const GoogleGIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" focusable="false">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

interface ProposalToken {
  proposal_id: string;
  business_id: string;
  token: string;
  expires_at: string;
  created_at: string;
}

interface Business {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  logo_url?: string;
  booking_settings?: {
    logo_url?: string;
  };
}

interface Customer {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address?: string | null;
}

interface Service {
  id?: string;
  line_key?: string | null;
  name: string;
  description: string;
  price: number; // This is the original price
  quantity: number;
  title: string;
  details?: string[];
  benefits?: string[];
  estimated_duration?: string;
  isSelectable?: boolean;
  is_selected?: boolean;
  isSelected?: boolean;
  discount_amount?: number;
  discount_type?: 'percentage' | 'dollar';
  discount_value?: number;
  offer?: {
    id: string;
    title: string;
    status: string;
    show_customer_address?: boolean;
  };
}

interface Product {
  id?: string;
  name: string;
  price: number;
  quantity: number;
  category_name?: string;
  stock_item_id?: string;
  discount_amount?: number;
  discount_type?: 'percentage' | 'dollar';
  discount_value?: number;
  offer?: {
    id: string;
    title: string;
    status: string;
    show_customer_address?: boolean;
  };
}

interface Proposal {
  id: string;
  business_id: string;
  customer_id: string;
  title: string;
  description: string;
  total_amount: number;
  status: 'draft' | 'sent' | 'viewed' | 'approved' | 'declined' | 'booked';
  valid_until: string;
  created_at: string;
  updated_at: string;
  services: Service[];
  products?: Product[];
  customer: Customer;
  notes?: string;
}

interface OfferProps {
  title: string;
  description?: string;
  partialMode?: boolean;
  customerAddress?: string | null;
  services: Service[];
  products?: Product[];
  productPhotos?: Record<string, string>;
  /** Per-service photo URLs keyed by the service's stable line_key. */
  servicePhotos?: Record<string, string[]>;
  isExpanded: boolean;
  isSelected: boolean;
  onToggle: () => void;
  onSelect: () => void;
  tax?: number;
  promo?: number;
  isSelecting?: boolean;
  depositPercentage?: number | null;
  businessTaxRates?: { name: string; rate: number }[];
  serviceAdditionalTaxes?: { service_id: string; name: string; rate: number }[];
}

const Offer: React.FC<OfferProps> = ({
  title,
  description = '',
  partialMode = false,
  customerAddress = null,
  services,
  products = [],
  productPhotos = {},
  servicePhotos = {},
  isExpanded,
  isSelected,
  onToggle,
  onSelect,
  tax = 0,
  promo = 0,
  isSelecting = false,
  depositPercentage = null,
  businessTaxRates = [],
  serviceAdditionalTaxes = []
}) => {
  // No local state for optional service selection - use database state directly
  // Add state for tracking service selection locally
  const [localServiceToggle, setLocalServiceToggle] = useState<Record<string, boolean>>({});
  const { open: openImageViewer } = useImageViewer();

  // Component for product icon - shows photo if available, otherwise green Package2 icon
  const ProductIcon = ({ product }: { product: Product }) => {
    const photoUrl = productPhotos[product.stock_item_id || ''];
    
    if (photoUrl) {
      return (
        <div className="h-16 w-16 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
          <img 
            src={photoUrl} 
            alt={product.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              // If image fails to load, show fallback icon
              e.currentTarget.style.display = 'none';
              const parent = e.currentTarget.parentElement;
              if (parent) {
                parent.innerHTML = '<div class="h-8 w-8 text-green-600"><svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16.5 9.4 7.55 4.24"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.29,7 12,12 20.71,7"/><line x1="12" x2="12" y1="22" y2="12"/></svg></div>';
                parent.className = "h-16 w-16 rounded-lg bg-green-100 flex items-center justify-center";
              }
            }}
          />
        </div>
      );
    }
    
    return (
      <div className="h-16 w-16 rounded-lg bg-green-100 flex items-center justify-center">
        <Package2 className="h-8 w-8 text-green-600" />
      </div>
    );
  };

  // Photos attached to a specific service line. `dimmed` greys them out when an
  // optional service is unselected (matching the greyed service styling).
  const renderServiceGallery = (service: Service, dimmed: boolean) => {
    const urls = service.line_key ? (servicePhotos[service.line_key] || []) : [];
    if (urls.length === 0) return null;
    return (
      <div className={`mt-3 sm:mt-4 transition-all duration-200 ${dimmed ? 'opacity-40 grayscale' : ''}`}>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {urls.map((u, i) => (
            <img
              key={i}
              src={u}
              alt=""
              loading="lazy"
              onClick={() => openImageViewer({ images: urls.map((url) => ({ src: url, caption: service.name })), index: i })}
              className="w-full h-20 sm:h-24 object-cover rounded-lg border border-gray-200 cursor-pointer"
            />
          ))}
        </div>
      </div>
    );
  };

  // Calculate totals based on database selection state AND local toggle state
  const serviceSubtotal = services.reduce((total, service) => {
    // Only include the service in the total if:
    // 1. It's not optional (isSelectable === false), OR
    // 2. It's optional AND selected in the database (is_selected === true) or local state
    const isOptionalByName = service.name?.includes('(Optional)') || false;
    let isSelectable = Boolean(service.isSelectable);
    
    // If the service name indicates it's optional but isSelectable isn't set, force it to true
    if (isOptionalByName && !isSelectable) {
      isSelectable = true;
    }
    
    // Check both database state and local toggle state
    const serviceId = service.id || `service-${services.indexOf(service)}`;
    const isSelected = service.is_selected === true || localServiceToggle[serviceId] === true;
    const includeInTotal = !isSelectable || (isSelectable && isSelected);
    
    if (!includeInTotal) {
      return total;
    }
    
    const price = typeof service.price === 'number' ? service.price : 0;
    const quantity = typeof service.quantity === 'number' ? service.quantity : 1;
    return total + (price * quantity);
  }, 0);

  // Calculate product subtotal
  const productSubtotal = products.reduce((total, product) => {
    const price = typeof product.price === 'number' ? product.price : 0;
    const quantity = typeof product.quantity === 'number' ? product.quantity : 1;
    return total + (price * quantity);
  }, 0);

  const subtotal = serviceSubtotal + productSubtotal;

  // Calculate total discount amount from individual services
  const serviceDiscountAmount = services.reduce((totalDiscount, service) => {
    const isOptionalByName = service.name?.includes('(Optional)') || false;
    let isSelectable = Boolean(service.isSelectable);
    
    if (isOptionalByName && !isSelectable) {
      isSelectable = true;
    }
    
    const serviceId = service.id || `service-${services.indexOf(service)}`;
    const isSelected = service.is_selected === true || localServiceToggle[serviceId] === true;
    const includeInTotal = !isSelectable || (isSelectable && isSelected);
    
    if (!includeInTotal) {
      return totalDiscount;
    }
    
    const discountAmount = typeof (service as any).discount_amount === 'number' ? (service as any).discount_amount : 0;
    return totalDiscount + discountAmount;
  }, 0);

  // Calculate total discount amount from products
  const productDiscountAmount = products.reduce((totalDiscount, product) => {
    const discountAmount = typeof product.discount_amount === 'number' ? product.discount_amount : 0;
    return totalDiscount + discountAmount;
  }, 0);

  const totalDiscountAmount = serviceDiscountAmount + productDiscountAmount;
  
  // Calculate tax on discounted amount
  const discountedSubtotal = subtotal - totalDiscountAmount;
  const combinedRate = businessTaxRates.reduce((sum, r) => sum + r.rate, 0) / 100;
  const businessTaxAmount = Math.round((discountedSubtotal * combinedRate) * 100) / 100;

  const offerAddlTaxMap = new Map<string, { name: string; rate: number; amount: number }>();
  for (const svc of services) {
    const svcId = (svc as any).service_id || svc.id;
    if (!svcId) continue;
    const svcNet = (svc.price || 0) - ((svc as any).discount_amount || 0);
    for (const atx of serviceAdditionalTaxes.filter(t => t.service_id === svcId)) {
      const amt = svcNet * atx.rate / 100;
      const key = `${atx.name}_${atx.rate}`;
      if (offerAddlTaxMap.has(key)) offerAddlTaxMap.get(key)!.amount += amt;
      else offerAddlTaxMap.set(key, { name: atx.name, rate: atx.rate, amount: amt });
    }
  }
  const offerAddlTaxLines = Array.from(offerAddlTaxMap.values()).map(t => ({ ...t, amount: Math.round(t.amount * 100) / 100 }));
  const offerAddlTaxTotal = offerAddlTaxLines.reduce((s, t) => s + t.amount, 0);

  const taxAmount = businessTaxAmount + offerAddlTaxTotal;
  const total = Math.round((discountedSubtotal - promo + taxAmount) * 100) / 100;
  
  // Calculate deposit amount if deposit percentage is set
  const depositAmount = depositPercentage && depositPercentage > 0 
    ? Math.round((total * depositPercentage / 100) * 100) / 100 
    : null;

  // Format currency with fallback
  const formatCurrency = (value: number) => {
    if (isNaN(value)) return '$0.00';
    return `$${value.toFixed(2)}`;
  };

  return (
    <div 
      className={`
        relative overflow-hidden
        bg-white
        border border-gray-300
        rounded-xl sm:rounded-2xl
        transition-all duration-300
        group
        ${isExpanded ? 'shadow-xl shadow-gray-200/50' : 'hover:shadow-lg hover:shadow-gray-200/50'}
        ${isSelected ? 'ring-2 ring-[#00CC4D]/50 ring-offset-2 ring-offset-black/50' : ''}
        text-[13px] sm:text-base
      `}
    >
      <div 
        className={`
          cursor-pointer transition-all duration-300
          ${isExpanded ? 'bg-gray-50' : 'hover:bg-gray-50/50'}
        `}
        onClick={onToggle}
      >
        <div className="flex items-center justify-between p-3 sm:p-5 gap-3 sm:gap-6">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="text-gray-700 bg-gray-100 rounded-full p-1.5 sm:p-2 shadow-inner border border-gray-200">
              {isExpanded ? <ChevronUp className="h-4 w-4 sm:h-5 sm:w-5" /> : <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5" />}
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 font-orbitron">{title}</h3>
              {customerAddress && (
                <p className="text-xs sm:text-sm text-gray-500 mt-0.5 sm:mt-1">{customerAddress}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 sm:gap-6">
            <div className="text-right">
              <p className="text-lg sm:text-2xl font-bold text-[#00CC4D] font-orbitron">{formatCurrency(total)}</p>
              <p className="text-xs sm:text-sm text-gray-600">inc. tax</p>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSelect();
              }}
              disabled={isSelecting}
              className={`
                relative px-4 sm:px-7 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium
                transition-all duration-300 font-orbitron
                before:absolute before:inset-0 before:rounded-full before:bg-gradient-to-r
                before:transition-all before:duration-300 before:opacity-90
                ${isSelected 
                  ? 'before:from-[#00CC4D] before:to-[#00B344] text-white shadow-lg shadow-[#00CC4D]/30 scale-95 ring-1 sm:ring-2 ring-[#00CC4D]/50 ring-offset-1 sm:ring-offset-2 ring-offset-black/50'
                  : isSelecting
                    ? 'before:from-gray-500 before:to-gray-600 text-white cursor-not-allowed opacity-75'
                  : 'before:from-[#00CC4D] before:to-[#00B344] text-white hover:scale-105 hover:shadow-lg hover:shadow-[#00CC4D]/30'
                }
                overflow-hidden group whitespace-nowrap
                disabled:hover:scale-100 disabled:hover:shadow-none
              `}
            >
              <span className="relative inline-flex flex-col items-center">
                {isSelecting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    SELECTING...
                  </>
                ) : (
                  <>
                    <span>{isSelected ? 'ACCEPTED' : 'ACCEPT'}</span>
                    {depositAmount && depositAmount > 0 && !isSelected && (
                      <span className="text-[10px] sm:text-xs mt-0.5 opacity-90">
                        Deposit: {formatCurrency(depositAmount)}
                      </span>
                    )}
                  </>
                )}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Partial mode: show a description snippet with an expand affordance while collapsed */}
      {!isExpanded && partialMode && description && (
        <div className="px-3 sm:px-5 pb-3 sm:pb-4 -mt-1">
          <p className="text-[12px] sm:text-sm text-gray-500 leading-relaxed line-clamp-2">{description}</p>
          <button
            onClick={onToggle}
            className="mt-1.5 inline-flex items-center gap-1 text-[12px] sm:text-sm font-medium text-[#00CC4D] hover:underline"
          >
            Expand to view more
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {isExpanded && (
        <div className="px-3 sm:px-6 pb-4 sm:pb-5 border-t border-gray-300 bg-gray-50/30">
          <div className="pt-1 sm:pt-2">
            <div className="space-y-2 sm:space-y-3">

              {/* Display non-selectable (main) services first */}
              {services
                .filter(service => !service.isSelectable && !service.name?.includes('(Optional)'))
                .map((service, index) => {
                  const serviceId = service.id || `service-${services.indexOf(service)}`;
                  return (
                    <div key={`main-${serviceId}`} className="pt-3 sm:pt-4 pb-3 sm:pb-4 bg-white border-2 border-green-400 rounded-lg shadow-sm">
                      <div className="px-3 sm:px-4">
                      <div className="flex items-start justify-between mb-2 sm:mb-3">
                        <div className="flex-1">
                            <h4 className="font-semibold text-[14px] sm:text-[16px] font-orbitron text-gray-900">
                            {service.name.replace(/ \(Optional\)/gi, '')}
                          </h4>
                        </div>
                        <div className="flex items-center">
                          {/* Show compare-at / discount pricing */}
                          {(() => {
                            const basePrice = typeof service.price === 'number' ? service.price : 0;
                            const discount = (service as any).discount_amount && (service as any).discount_amount > 0 ? (service as any).discount_amount : 0;
                            const net = basePrice - discount;
                            const compareAt = (service as any).compare_at_price;
                            const hasCompare = compareAt != null && Number(compareAt) > basePrice;
                            const struck = hasCompare ? Number(compareAt) : (discount > 0 ? basePrice : null);
                            if (struck == null) {
                              return (
                                <div className="text-lg font-bold mr-3 text-[#00CC4D]">
                                  {formatCurrency(basePrice)}
                                </div>
                              );
                            }
                            return (
                              <div className="text-right mr-3">
                                <div className="text-sm text-gray-500 line-through">
                                  {formatCurrency(struck)}
                                </div>
                                <div className="text-lg font-bold text-[#00CC4D]">
                                  {formatCurrency(net)}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                        {service.description && (
                          <div className="flex items-start gap-1.5 sm:gap-2 text-[13px] sm:text-[15px] leading-relaxed col-span-full mb-3 sm:mb-4">
                            <div className="w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full mt-2 opacity-80 flex-shrink-0 bg-[#00CC4D]" />
                              <div className="text-gray-700">
                              <RichTextDescription 
                                description={service.description} 
                                  className="prose-sm text-gray-700 prose-headings:text-gray-800 prose-strong:text-gray-800 prose-em:text-gray-600" 
                              />
                            </div>
                          </div>
                        )}
                        {service.details && service.details.map((detail, detailIndex) => (
                          <div key={detailIndex} className="flex items-start gap-1.5 sm:gap-2 text-[13px] sm:text-[15px] leading-relaxed">
                            <div className="w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full mt-2 opacity-80 flex-shrink-0 bg-[#00CC4D]" />
                              <p className="text-gray-700">{detail}</p>
                          </div>
                        ))}
                      </div>

                      {service.benefits && service.benefits.length > 0 && (
                        <div className="mt-4 sm:mt-5">
                            <h5 className="font-semibold mb-2 sm:mb-3 text-[13px] sm:text-[15px] font-orbitron text-gray-900">Key Benefits</h5>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                            {service.benefits.map((benefit, benefitIndex) => (
                              <div key={benefitIndex} className="flex items-start gap-1.5 sm:gap-2 text-[13px] sm:text-[15px] leading-relaxed">
                                <div className="w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full mt-2 opacity-80 flex-shrink-0 bg-[#00CC4D]" />
                                  <p className="text-gray-700">{benefit}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {service.estimated_duration && (
                        <div className="mt-4 sm:mt-5">
                            <h5 className="font-semibold mb-2 sm:mb-3 text-[13px] sm:text-[15px] font-orbitron text-gray-900">Estimated Duration</h5>
                            <p className="text-[13px] sm:text-[15px] leading-relaxed text-gray-700">{service.estimated_duration}</p>
                        </div>
                      )}
                      {renderServiceGallery(service, false)}
                      </div>
                    </div>
                  );
                })
              }
              
              {/* Add the separator for optional services if we have both types */}
              {services.some(s => !s.isSelectable && !s.name?.includes('(Optional)')) && 
               services.some(s => s.isSelectable || s.name?.includes('(Optional)')) && (
                <div className="border-t border-gray-200/80 mt-4 pt-4 mb-3">
                  <div className="text-center px-3 py-2 bg-[#e8f5e9] rounded-md border border-[#c8e6c9] text-sm font-medium text-gray-700 shadow-sm">
                    <span className="inline-flex items-center">
                      Select Any Additional Services Below Before Accepting An Offer
                    </span>
                  </div>
                </div>
              )}
              
              {/* Then display all optional services */}
              {services
                .filter(service => service.isSelectable || service.name?.includes('(Optional)'))
                .map((service, index) => {
                  const serviceId = service.id || `service-${services.indexOf(service)}`;
                  const isSelected = service.is_selected === true || localServiceToggle[serviceId] === true;
                  
                  // Only hide optional services that aren't selected when the offer is expanded
                  if (!isSelected && !isExpanded) {
                    return null;
                  }
                  
                  return (
                    <div key={`optional-${serviceId}`} className={`pt-3 sm:pt-4 pb-3 sm:pb-4 rounded-lg border-2 transition-all duration-200 ${isSelected ? 'bg-gray-50 border-green-400 shadow-sm' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="px-3 sm:px-4">
                      <div className="flex items-start justify-between mb-2 sm:mb-3">
                        <div className="flex-1">
                            <h4 className={`font-semibold text-[14px] sm:text-[16px] font-orbitron ${isSelected ? 'text-gray-900' : 'text-gray-600'}`}>
                            {service.name.replace(/ \(Optional\)/gi, '')}
                          </h4>
                        </div>
                        <div className="flex items-center">
                          {/* Show compare-at / discount pricing */}
                          {(() => {
                            const basePrice = typeof service.price === 'number' ? service.price : 0;
                            const discount = (service as any).discount_amount && (service as any).discount_amount > 0 ? (service as any).discount_amount : 0;
                            const net = basePrice - discount;
                            const compareAt = (service as any).compare_at_price;
                            const hasCompare = compareAt != null && Number(compareAt) > basePrice;
                            const struck = hasCompare ? Number(compareAt) : (discount > 0 ? basePrice : null);
                            if (struck != null) {
                              return (
                                <div className="text-right mr-3">
                                  <div className={`text-sm line-through ${!isSelected ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {formatCurrency(struck)}
                                  </div>
                                  <div className={`text-lg font-bold ${!isSelected ? 'text-gray-400' : 'text-[#00CC4D]'}`}>
                                    {formatCurrency(net)}
                                  </div>
                                </div>
                              );
                            }
                            return (
                              <div className={`text-lg font-bold mr-3 ${!isSelected ? 'text-gray-400' : 'text-[#00CC4D]'}`}>
                                {formatCurrency(typeof service.price === 'number' ? service.price : 0)}
                              </div>
                            );
                          })()}
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log(`Toggle clicked for ${service.name}`);
                              if (supabase && serviceId) {
                                const newState = !isSelected;
                                console.log(`Updating service ${serviceId} to ${newState}`);
                                supabase
                                  .from('proposal_services')
                                  .update({ is_selected: newState })
                                  .eq('id', serviceId)
                                  .then(result => {
                                    console.log("Toggle result:", result);
                                    if (!result.error) {
                                      // Update the local state instead of reloading the page
                                      service.is_selected = newState;
                                      // Force re-render by updating a state variable
                                      setLocalServiceToggle(prev => ({
                                        ...prev,
                                        [serviceId]: newState
                                      }));
                                    } else {
                                      console.error("Error toggling service:", result.error);
                                    }
                                  });
                              }
                            }}
                            className={`
                              relative w-12 h-6 rounded-full p-1 flex transition-all duration-300 shadow-inner
                              ${isSelected ? 'bg-[#00CC4D] justify-end' : 'bg-white justify-start'}
                              border-2 ${isSelected ? 'border-[#00CC4D]' : 'border-[#00CC4D]/40'}
                              cursor-pointer hover:border-[#00CC4D] hover:shadow-md
                              outline outline-2 outline-offset-2 ${isSelected ? 'outline-[#00CC4D]/20' : 'outline-[#00CC4D]/10'}
                            `}
                            title={isSelected ? "Click to deselect" : "Click to select"}
                          >
                            <div className={`
                              w-4 h-4 rounded-full transition-all duration-300 flex items-center justify-center
                              ${isSelected ? 'bg-white text-[#00CC4D]' : 'bg-[#00CC4D]/40 text-white'}
                              hover:bg-[#00CC4D]
                            `}>
                              {isSelected && <span className="text-xs">✓</span>}
                            </div>
                          </button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                        {service.description && (
                          <div className="flex items-start gap-1.5 sm:gap-2 text-[13px] sm:text-[15px] leading-relaxed col-span-full mb-3 sm:mb-4">
                            <div className={`w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full mt-2 opacity-80 flex-shrink-0 ${!isSelected ? 'bg-gray-300' : 'bg-[#00CC4D]'}`} />
                              <div className={`${isSelected ? 'text-gray-700' : 'text-gray-500'}`}>
                              <RichTextDescription 
                                description={service.description} 
                                  className={`prose-sm ${isSelected ? 'text-gray-700 prose-headings:text-gray-800 prose-strong:text-gray-800 prose-em:text-gray-600' : 'text-gray-500 prose-headings:text-gray-600 prose-strong:text-gray-600 prose-em:text-gray-400'}`} 
                              />
                            </div>
                          </div>
                        )}
                        {service.details && service.details.map((detail, detailIndex) => (
                          <div key={detailIndex} className="flex items-start gap-1.5 sm:gap-2 text-[13px] sm:text-[15px] leading-relaxed">
                            <div className={`w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full mt-2 opacity-80 flex-shrink-0 ${!isSelected ? 'bg-gray-300' : 'bg-[#00CC4D]'}`} />
                              <p className={`${isSelected ? 'text-gray-700' : 'text-gray-500'}`}>{detail}</p>
                          </div>
                        ))}
                      </div>

                      {service.benefits && service.benefits.length > 0 && (
                        <div className="mt-4 sm:mt-5">
                            <h5 className={`font-semibold mb-2 sm:mb-3 text-[13px] sm:text-[15px] font-orbitron ${isSelected ? 'text-gray-900' : 'text-gray-600'}`}>Key Benefits</h5>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                            {service.benefits.map((benefit, benefitIndex) => (
                              <div key={benefitIndex} className="flex items-start gap-1.5 sm:gap-2 text-[13px] sm:text-[15px] leading-relaxed">
                                <div className={`w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full mt-2 opacity-80 flex-shrink-0 ${!isSelected ? 'bg-gray-300' : 'bg-[#00CC4D]'}`} />
                                  <p className={`${isSelected ? 'text-gray-700' : 'text-gray-500'}`}>{benefit}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {service.estimated_duration && (
                        <div className="mt-4 sm:mt-5">
                            <h5 className={`font-semibold mb-2 sm:mb-3 text-[13px] sm:text-[15px] font-orbitron ${isSelected ? 'text-gray-900' : 'text-gray-600'}`}>Estimated Duration</h5>
                            <p className={`text-[13px] sm:text-[15px] leading-relaxed ${isSelected ? 'text-gray-700' : 'text-gray-500'}`}>{service.estimated_duration}</p>
                        </div>
                      )}
                      {renderServiceGallery(service, !isSelected)}
                      </div>
                    </div>
                  );
                })
              }

              {/* Products Section */}
              {products && products.length > 0 && (
                <>
                  {/* Add separator if we have services */}
                  {services.length > 0 && (
                    <div className="relative my-6">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-200/80"></div>
                      </div>
                      <div className="relative flex justify-center">
                        <div className="bg-gray-50 px-2">
                          <Package2 className="h-4 w-4 text-gray-400" />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Display all products */}
                  {products.map((product, index) => {
                    const productId = product.id || `product-${index}`;
                    return (
                      <div key={`product-${productId}`} className="pt-3 sm:pt-4 pb-3 sm:pb-4 bg-white border-2 border-green-400 rounded-lg shadow-sm">
                        <div className="px-3 sm:px-4">
                          <div className="flex items-start justify-between mb-2 sm:mb-3">
                            <div className="flex items-start">
                              <div className="flex-shrink-0 mr-3">
                                <ProductIcon product={product} />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-semibold text-[14px] sm:text-[16px] font-orbitron text-gray-900">
                                  {product.name}
                                </h4>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-[12px] sm:text-[13px] text-gray-600">
                                    Quantity: {product.quantity}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center">
                              {/* Show discount pricing if product has discount */}
                              {product.discount_amount && product.discount_amount > 0 ? (
                                <div className="text-right">
                                  <div className="text-sm text-gray-500 line-through">
                                    {formatCurrency(typeof product.price === 'number' ? product.price * product.quantity : 0)}
                                  </div>
                                  <div className="text-lg font-bold text-[#00CC4D]">
                                    {formatCurrency((typeof product.price === 'number' ? product.price * product.quantity : 0) - (product.discount_amount || 0))}
                                  </div>
                                </div>
                              ) : (
                                <div className="text-lg font-bold text-[#00CC4D]">
                                  {formatCurrency(typeof product.price === 'number' ? product.price * product.quantity : 0)}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}

              <div className="border-t border-gray-200/80 pt-4 sm:pt-5 mt-4">
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex justify-between text-[13px] sm:text-[15px] text-gray-600">
                    <span>Subtotal</span>
                    <span>{formatCurrency(subtotal)}</span>
                  </div>
                  {totalDiscountAmount > 0 && (
                    <div className="flex justify-between text-[13px] sm:text-[15px] text-green-600">
                      <span>Discount</span>
                      <span>-{formatCurrency(totalDiscountAmount)}</span>
                    </div>
                  )}
                  {promo > 0 && (
                    <div className="flex justify-between text-[13px] sm:text-[15px] text-blue-600">
                      <span>Promo</span>
                      <span>-{formatCurrency(promo)}</span>
                    </div>
                  )}
                  {businessTaxRates.map((rate, ri) => (
                    <div key={ri} className="flex justify-between text-[13px] sm:text-[15px] text-gray-600">
                      <span>{rate.name} ({rate.rate}%)</span>
                      <span>+{formatCurrency(Math.round((discountedSubtotal * rate.rate / 100) * 100) / 100)}</span>
                    </div>
                  ))}
                  {offerAddlTaxLines.map((line, li) => (
                    <div key={`addl-${li}`} className="flex justify-between text-[13px] sm:text-[15px] text-gray-600">
                      <span>{line.name} ({line.rate}%)</span>
                      <span>+{formatCurrency(line.amount)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-base sm:text-lg font-bold pt-3 mt-1 border-t border-gray-200/50">
                    <span className="text-gray-800 font-orbitron">Total</span>
                    <span className="text-gray-800 font-orbitron">{formatCurrency(total)}</span>
                  </div>
                  
                  {/* Deposit Information */}
                  {depositAmount && depositAmount > 0 && (
                    <>
                      <div className="flex justify-between text-sm sm:text-base font-medium pt-2 mt-1 border-t border-gray-200/50">
                        <span className="text-gray-800 font-orbitron">Deposit Required ({depositPercentage}%)</span>
                        <span className="text-gray-800 font-orbitron">{formatCurrency(depositAmount)}</span>
                      </div>
                      <div className="flex justify-between text-xs sm:text-sm text-gray-500">
                        <span>Remaining After Deposit</span>
                        <span>{formatCurrency(total - depositAmount)}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Second Select Button at Bottom */}
                <div className="pt-4 sm:pt-5 mt-4 border-t border-gray-200/50">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect();
                    }}
                    disabled={isSelecting}
                    className={`
                      w-full relative px-4 sm:px-7 py-3 sm:py-4 rounded-full text-sm sm:text-base font-medium
                      transition-all duration-300 font-orbitron
                      before:absolute before:inset-0 before:rounded-full before:bg-gradient-to-r
                      before:transition-all before:duration-300 before:opacity-90
                      ${isSelected 
                        ? 'before:from-[#00CC4D] before:to-[#00B344] text-white shadow-lg shadow-[#00CC4D]/30 scale-95 ring-1 sm:ring-2 ring-[#00CC4D]/50 ring-offset-1 sm:ring-offset-2 ring-offset-black/50'
                        : isSelecting
                          ? 'before:from-gray-500 before:to-gray-600 text-white cursor-not-allowed opacity-75'
                          : 'before:from-[#00CC4D] before:to-[#00B344] text-white hover:scale-105 hover:shadow-lg hover:shadow-[#00CC4D]/30'
                      }
                      overflow-hidden group
                      disabled:hover:scale-100 disabled:hover:shadow-none
                    `}
                  >
                    <span className="relative inline-flex flex-col items-center justify-center">
                      {isSelecting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          SELECTING...
                        </>
                      ) : (
                        <>
                          <span>{isSelected ? 'ACCEPTED' : 'ACCEPT THIS OFFER'}</span>
                          {depositAmount && depositAmount > 0 && !isSelected && (
                            <span className="text-xs mt-1 opacity-90">
                              Deposit Required: {formatCurrency(depositAmount)}
                            </span>
                          )}
                        </>
                      )}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};



// Add this utility function at the top level of the file or inside the component
const fetchSelectedServicesForProposal = async (supabaseClient: any, proposalId: string) => {
  if (!supabaseClient) return null;
  
  try {
    // First, find the approved offer ID
    const { data: proposalData, error: proposalError } = await supabaseClient
      .from('proposals')
      .select('notes')
      .eq('id', proposalId)
      .single();
      
    if (proposalError || !proposalData?.notes) {
      console.error("Error fetching proposal notes:", proposalError);
      return null;
    }
    
    // Parse the notes to get the approved offer ID
    let approvedOfferId: string | null = null;
    try {
      const notes = JSON.parse(proposalData.notes);
      approvedOfferId = notes.approved_offer_id;
    } catch (e) {
      console.error("Error parsing notes:", e);
    }
    
    if (!approvedOfferId) {
      console.error("No approved offer ID found in notes");
      return null;
    }
    
    // Fetch all services for this proposal and offer that are selected
    // Use proposal_services table directly to ensure we get discount information
    const { data: servicesData, error: servicesError } = await supabaseClient
      .from('proposal_services')
      .select(`
        id, name, description, price, quantity, is_selected, isSelectable, is_selectable,
        discount_amount, discount_type, discount_value,
        services!inner(name, description, price)
      `)
      .eq('proposal_id', proposalId)
      .eq('is_selected', true);
      
    if (servicesError) {
      console.error("Error fetching selected services:", servicesError);
      return null;
    }
    
    console.log(`Found ${servicesData.length} selected services for proposal ${proposalId}`);
    
    // Make sure all required properties are available for display
    const processedServices = servicesData.map((service: any) => {
      // Use proposal service data first, fallback to joined service data
      const serviceInfo = service.services || {};
      
      return {
        id: service.id,
        name: service.name || serviceInfo.name || 'Unknown Service',
        description: service.description || serviceInfo.description || '',
        price: typeof service.price === 'number' ? service.price : (typeof serviceInfo.price === 'number' ? serviceInfo.price : 0),
        quantity: typeof service.quantity === 'number' ? service.quantity : 1,
        is_selected: service.is_selected,
        isSelectable: service.isSelectable || service.is_selectable, // Map from either property
        // Ensure discount information is preserved
        discount_amount: typeof service.discount_amount === 'number' ? service.discount_amount : 0,
        discount_type: service.discount_type || null,
        discount_value: typeof service.discount_value === 'number' ? service.discount_value : null
      };
    });
    

    
    return { 
      services: processedServices,
      approvedOfferId
    };
  } catch (err) {
    console.error("Error in fetchSelectedServicesForProposal:", err);
    return null;
  }
};

// Add proper types for the ThankYouPage component props
interface ThankYouPageProps {
  proposal: Proposal;
  business: Business;
  selectedOffer: string | null;
  supabase: any;
}

// Add a new DeclinePage component
interface DeclinePageProps {
  business: Business;
}

const DeclinePage: React.FC<DeclinePageProps> = ({ business }) => {
  return (
    <div className="min-h-screen bg-black">
      <div className="relative max-w-4xl mx-auto px-3 sm:px-6 py-8 sm:py-12">
        <div className="text-center space-y-6">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-red-500/20 text-red-400">
            <X className="w-8 h-8 sm:w-10 sm:h-10" />
          </div>
          
          <div className="space-y-2">
            <h2 className="text-2xl sm:text-3xl font-bold text-white font-orbitron">Proposal Declined</h2>
            <p className="text-blue-200/70 text-sm sm:text-base">
              You have declined all offers in this proposal. If you'd like to discuss other options, please contact us directly.
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-white/10 p-4 sm:p-6 mt-8">
            <h3 className="text-sm sm:text-base font-medium text-blue-100 mb-3">Next Steps</h3>
            <p className="text-blue-200/70 text-sm leading-relaxed">
              We appreciate you taking the time to review our proposal. If you have any questions or would like to discuss alternative solutions, please don't hesitate to reach out.
            </p>
          </div>

          <div className="text-center mt-8 text-xs sm:text-sm text-blue-200/70">
            <p className="space-y-1 sm:space-y-0">
              <span className="block sm:inline">Contact {business?.name || 'the business'}</span>
              {business?.email && (
                <span className="block sm:inline sm:ml-1">
                  at <a href={`mailto:${business.email}`} className="text-blue-400 hover:text-blue-300 transition-colors">{business.email}</a>
                  {business?.phone && (
                    <> or call at <a href={`tel:${business.phone}`} className="text-blue-400 hover:text-blue-300 transition-colors">{business.phone}</a></>
                  )}
                </span>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Update the ThankYouPage component with proper types
const ThankYouPage: React.FC<ThankYouPageProps> = ({ proposal, business, selectedOffer, supabase }) => {
  const [selectedServices, setSelectedServices] = useState<Service[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [productPhotos, setProductPhotos] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false); // Start as false since we pre-calculate
  const [error, setError] = useState<string | null>(null);
  const [customThankYouMessage, setCustomThankYouMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!business?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error: rpcError } = await supabase.rpc('get_proposal_thank_you_message', {
          p_business_id: business.id,
        });
        if (cancelled || rpcError || !data) return;
        const enabled = data.enabled === true || data.enabled === 'true';
        const message = typeof data.message === 'string' ? data.message.trim() : '';
        if (enabled && message) {
          setCustomThankYouMessage(message);
        }
      } catch (err) {
        console.warn('Could not load proposal thank you message:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [business?.id, supabase]);

  useEffect(() => {
    // **INSTANT DATA PROCESSING** - Use existing proposal data instead of fetching
    const processSelectedData = () => {
      console.log("🚀 Processing selected services and products instantly from existing data");
      
      if (!proposal || !selectedOffer) {
        console.log("Missing proposal or selectedOffer");
        setSelectedServices([]);
        setSelectedProducts([]);
        return;
      }
        
      // Filter services for the selected offer
      const offerServices = proposal.services.filter(service => 
        service.offer?.id === selectedOffer
      );
      
      console.log(`Found ${offerServices.length} services for selected offer`);
      
      // Include: all required services + selected optional services
      const servicesToShow = offerServices.filter(service => {
        const isOptional = service.isSelectable === true;
        const isSelected = service.is_selected === true;
        
        // Include if: (not optional) OR (optional AND selected)
        const shouldInclude = !isOptional || (isOptional && isSelected);
        
        console.log(`Service ${service.name}: optional=${isOptional}, selected=${isSelected}, include=${shouldInclude}`);
        return shouldInclude;
      });
      
      // Filter products for the selected offer
      const offerProducts = proposal.products?.filter(product => 
        product.offer?.id === selectedOffer
      ) || [];
      
      console.log(`Found ${offerProducts.length} products for selected offer`);
      console.log(`✅ Showing ${servicesToShow.length} services and ${offerProducts.length} products instantly`);

      setSelectedServices(servicesToShow);
      setSelectedProducts(offerProducts);
      
      // Load product photos if we have products
      if (offerProducts.length > 0) {
        loadProductPhotos(offerProducts);
      }
    };
    
    const loadProductPhotos = async (products: Product[]) => {
      try {
        const stockItemIds = products
          .map(product => product.stock_item_id)
          .filter(Boolean) as string[];
          
        if (stockItemIds.length > 0) {
          const photoMap = await ProductPhotoService.getPhotosByStockItemIds(stockItemIds);
          const photoUrls: Record<string, string> = {};
          
          if (photoMap && typeof photoMap === 'object') {
            Object.entries(photoMap).forEach(([itemId, photo]) => {
              if (photo && photo.photo_url) {
                photoUrls[itemId] = photo.photo_url;
              }
            });
          }
          
          setProductPhotos(photoUrls);
          console.log('✅ Product photos loaded for thank you page:', Object.keys(photoUrls).length);
        }
      } catch (photoError) {
        console.error('Error loading product photos for thank you page:', photoError);
        setProductPhotos({});
      }
    };
        
    processSelectedData();
  }, [proposal, selectedOffer]);

  // Component for product icon in thank you page - shows photo if available, otherwise green Package2 icon
  const ProductIcon = ({ product }: { product: Product }) => {
    const photoUrl = productPhotos[product.stock_item_id || ''];
    
    if (photoUrl) {
      return (
        <div className="h-8 w-8 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
          <img 
            src={photoUrl} 
            alt={product.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              // If image fails to load, show fallback icon
              e.currentTarget.style.display = 'none';
              const parent = e.currentTarget.parentElement;
              if (parent) {
                parent.innerHTML = '<div class="h-4 w-4 text-green-400"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16.5 9.4 7.55 4.24"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.29,7 12,12 20.71,7"/><line x1="12" x2="12" y1="22" y2="12"/></svg></div>';
                parent.className = "h-8 w-8 rounded-lg bg-green-100/20 flex items-center justify-center";
              }
            }}
          />
        </div>
      );
    }
    
    return (
      <div className="h-8 w-8 rounded-lg bg-green-100/20 flex items-center justify-center">
        <Package2 className="h-4 w-4 text-green-400" />
      </div>
    );
  };

  // Calculate total amount safely with proper currency rounding
  const serviceSubtotal = Math.round(selectedServices.reduce(
    (sum, service) => sum + (typeof service.price === 'number' ? service.price : 0), 
    0
  ) * 100) / 100;
  
  const productSubtotal = Math.round(selectedProducts.reduce(
    (sum, product) => sum + (typeof product.price === 'number' ? product.price * product.quantity : 0), 
    0
  ) * 100) / 100;
  
  const subtotal = serviceSubtotal + productSubtotal;
  
  // Calculate total discount amount
  const serviceDiscountAmount = Math.round(selectedServices.reduce(
    (sum, service) => sum + ((service as any).discount_amount || 0),
    0
  ) * 100) / 100;
  
  const productDiscountAmount = Math.round(selectedProducts.reduce(
    (sum, product) => sum + (product.discount_amount || 0),
    0
  ) * 100) / 100;
  
  const totalDiscountAmount = serviceDiscountAmount + productDiscountAmount;
  
  // Calculate discounted subtotal
  const discountedSubtotal = Math.round((subtotal - totalDiscountAmount) * 100) / 100;
  
  const [businessTaxRates, setBusinessTaxRates] = useState<{ name: string; rate: number }[]>([]);
  useEffect(() => {
    if (business?.id) {
      supabase
        .from('tax_rates')
        .select('name, rate')
        .eq('business_id', business.id)
        .then(({ data }: any) => { if (data) setBusinessTaxRates(data); });
    }
  }, [business?.id]);
  const combinedTaxRate = businessTaxRates.reduce((sum, r) => sum + r.rate, 0) / 100;
  const businessTaxAmt = Math.round((discountedSubtotal * combinedTaxRate) * 100) / 100;

  const [tyServiceAddlTaxes, setTyServiceAddlTaxes] = useState<{ service_id: string; name: string; rate: number }[]>([]);
  useEffect(() => {
    const svcIds = [...new Set(selectedServices.map(s => s.id).filter(Boolean))];
    if (svcIds.length === 0) { setTyServiceAddlTaxes([]); return; }
    supabase
      .from('service_additional_taxes')
      .select('service_id, name, rate')
      .in('service_id', svcIds)
      .then(({ data }: any) => { if (data) setTyServiceAddlTaxes(data); });
  }, [selectedServices.map(s => s.id).join(',')]);

  const tyAddlTaxMap = new Map<string, { name: string; rate: number; amount: number }>();
  for (const svc of selectedServices) {
    const svcId = svc.id;
    if (!svcId) continue;
    const svcNet = (svc.price || 0) - ((svc as any).discount_amount || 0);
    for (const atx of tyServiceAddlTaxes.filter(t => t.service_id === svcId)) {
      const amt = svcNet * atx.rate / 100;
      const key = `${atx.name}_${atx.rate}`;
      if (tyAddlTaxMap.has(key)) tyAddlTaxMap.get(key)!.amount += amt;
      else tyAddlTaxMap.set(key, { name: atx.name, rate: atx.rate, amount: amt });
    }
  }
  const tyAddlTaxLines = Array.from(tyAddlTaxMap.values()).map(t => ({ ...t, amount: Math.round(t.amount * 100) / 100 }));
  const tyAddlTaxTotal = tyAddlTaxLines.reduce((s, t) => s + t.amount, 0);

  const taxAmount = businessTaxAmt + tyAddlTaxTotal;
  const totalAmount = Math.round((discountedSubtotal + taxAmount) * 100) / 100;
  
  // Fetch deposit information for the selected offer
  const [depositInfo, setDepositInfo] = useState<{ amount: number; percentage: number } | null>(null);
  
  useEffect(() => {
    const fetchDepositInfo = async () => {
      if (!selectedOffer || !supabase || !proposal) return;
      
      try {
        // Get deposit payment for this proposal
        const { data: depositPayment, error: depositError } = await supabase
          .from('payments')
          .select('amount, payment_type')
          .eq('proposal_id', proposal.id)
          .eq('payment_type', 'deposit')
          .eq('status', 'completed')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
        
        if (!depositError && depositPayment) {
          // Get deposit percentage from offer
          const { data: offerData } = await supabase
            .from('proposal_offers')
            .select('deposit_percentage')
            .eq('id', selectedOffer)
            .single();
          
          if (offerData?.deposit_percentage) {
            setDepositInfo({
              amount: depositPayment.amount,
              percentage: offerData.deposit_percentage
            });
          }
        }
      } catch (err) {
        console.error('Error fetching deposit info:', err);
      }
    };
    
    fetchDepositInfo();
  }, [selectedOffer, proposal, supabase]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-950 to-black flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-800/60 border border-gray-700/50 flex items-center justify-center">
            <FileText className="w-9 h-9 text-gray-500" />
          </div>
          <h2 className="text-2xl font-semibold text-white mb-3">Proposal Unavailable</h2>
          <p className="text-gray-400 text-base leading-relaxed">
            This proposal has either expired or is no longer available. If you believe this is a mistake, please contact the business that sent you this link.
          </p>
        </div>
      </div>
    );
  }

  if (!proposal || !business) {
    // Always show loading state initially, even if isLoading is false (might be a race condition)
    // Only show error if we've explicitly set an error and we're not loading
    if (isLoading || (!error && !proposal && !business)) {
      return (
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent mx-auto mb-4" />
            <p className="text-blue-200/70 text-sm">Loading proposal...</p>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-950 to-black flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-800/60 border border-gray-700/50 flex items-center justify-center">
            <FileText className="w-9 h-9 text-gray-500" />
          </div>
          <h2 className="text-2xl font-semibold text-white mb-3">Proposal Unavailable</h2>
          <p className="text-gray-400 text-base leading-relaxed mb-6">
            This proposal has either expired or is no longer available. If you believe this is a mistake, please contact the business that sent you this link.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Show the decline page if status is declined
  if (proposal.status === 'declined') {
    return <DeclinePage business={business} />;
  }

  // Show thank you page if approved or booked
  if (proposal.status === 'approved' || proposal.status === 'booked') {
    return (
      <div className="min-h-screen bg-black">
        <div className="relative max-w-4xl mx-auto px-3 sm:px-6 py-8 sm:py-12">
          <div className="text-center space-y-6">
            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#00CC4D]/20 text-[#00CC4D]">
              <svg className="w-8 h-8 sm:w-10 sm:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            <div className="space-y-2">
              <h2 className="text-2xl sm:text-3xl font-bold text-white font-orbitron">Thank You!</h2>
              <p className="text-blue-200/70 text-sm sm:text-base">
                We've received your selection and will reach out to you shortly to proceed with the next steps.
              </p>
              {error && (
                <p className="text-xs text-yellow-300 mt-1">Error: {error}</p>
              )}
            </div>

            <div className="bg-white/5 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-white/10 p-4 sm:p-6 mt-8">
              <h3 className="text-sm sm:text-base font-medium text-blue-100 mb-3">
                {selectedProducts.length > 0 ? 'Selected Services & Products' : 'Selected Services'}
              </h3>
              
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
                </div>
              ) : (selectedServices.length > 0 || selectedProducts.length > 0) ? (
                <>
                  {selectedServices.map((service, index) => (
                    <div key={index} className="text-left mb-4 last:mb-0 pb-4 last:pb-0 border-b border-white/10 last:border-b-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-white font-medium">{service.name.replace(/ \(Optional\)/gi, '')}</p>
                          </div>
                                        {service.description && (
                <div className="mt-1">
                  <RichTextDescription 
                    description={service.description} 
                    className="prose-sm text-blue-200/70 prose-headings:text-blue-200 prose-strong:text-blue-100 prose-em:text-blue-300/70" 
                  />
                </div>
              )}
                        </div>
                        <div className="text-right">
                          {/* Show compare-at / discount pricing */}
                          {(() => {
                            const basePrice = typeof service.price === 'number' ? service.price : 0;
                            const discount = (service as any).discount_amount && (service as any).discount_amount > 0 ? (service as any).discount_amount : 0;
                            const net = basePrice - discount;
                            const compareAt = (service as any).compare_at_price;
                            const hasCompare = compareAt != null && Number(compareAt) > basePrice;
                            const struck = hasCompare ? Number(compareAt) : (discount > 0 ? basePrice : null);
                            if (struck != null) {
                              return (
                                <div>
                                  <p className="text-sm text-blue-300/60 line-through">
                                    ${struck.toFixed(2)}
                                  </p>
                                  <p className="text-lg font-bold text-[#00CC4D]">
                                    ${net.toFixed(2)}
                                  </p>
                                </div>
                              );
                            }
                            return (
                              <p className="text-lg font-bold text-[#00CC4D]">
                                ${typeof service.price === 'number' ? service.price.toFixed(2) : '0.00'}
                              </p>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Products Section */}
                  {selectedProducts.length > 0 && (
                    <>
                      {selectedServices.length > 0 && (
                        <div className="my-4 border-t border-white/10 pt-4">
                          <h4 className="text-sm font-medium text-blue-100 mb-3">Products</h4>
                        </div>
                      )}
                      {selectedProducts.map((product, index) => (
                        <div key={index} className="text-left mb-4 last:mb-0 pb-4 last:pb-0 border-b border-white/10 last:border-b-0">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start">
                              <div className="flex-shrink-0 mr-3">
                                <ProductIcon product={product} />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="text-white font-medium">{product.name}</p>
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-xs text-blue-200/70">
                                    Quantity: {product.quantity}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              {/* Show discount pricing if product has discount */}
                              {product.discount_amount && product.discount_amount > 0 ? (
                                <div>
                                  <p className="text-sm text-blue-300/60 line-through">
                                    ${(typeof product.price === 'number' ? product.price * product.quantity : 0).toFixed(2)}
                                  </p>
                                  <p className="text-lg font-bold text-[#00CC4D]">
                                    ${((typeof product.price === 'number' ? product.price * product.quantity : 0) - (product.discount_amount || 0)).toFixed(2)}
                                  </p>
                                </div>
                              ) : (
                                <p className="text-lg font-bold text-[#00CC4D]">
                                  ${(typeof product.price === 'number' ? product.price * product.quantity : 0).toFixed(2)}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm sm:text-base">
                        <span className="text-blue-200/70">Subtotal:</span>
                        <span className="text-[#00CC4D]">
                          ${subtotal.toFixed(2)}
                        </span>
                      </div>
                      {totalDiscountAmount > 0 && (
                        <div className="flex justify-between text-sm sm:text-base">
                          <span className="text-blue-200/70">Discount:</span>
                          <span className="text-[#00CC4D]">
                            -${totalDiscountAmount.toFixed(2)}
                          </span>
                        </div>
                      )}
                      {businessTaxRates.map((rate, ri) => (
                        <div key={ri} className="flex justify-between text-sm sm:text-base">
                          <span className="text-blue-200/70">{rate.name} ({rate.rate}%):</span>
                          <span className="text-[#00CC4D]">
                            ${(Math.round((discountedSubtotal * rate.rate / 100) * 100) / 100).toFixed(2)}
                          </span>
                        </div>
                      ))}
                      {tyAddlTaxLines.map((line, li) => (
                        <div key={`ty-addl-${li}`} className="flex justify-between text-sm sm:text-base">
                          <span className="text-blue-200/70">{line.name} ({line.rate}%):</span>
                          <span className="text-[#00CC4D]">
                            ${line.amount.toFixed(2)}
                          </span>
                        </div>
                      ))}
                      <div className="flex justify-between text-sm sm:text-base pt-2 border-t border-white/10">
                        <span className="text-blue-200/70 font-medium">Total:</span>
                        <span className="text-lg font-bold text-[#00CC4D]">
                          ${totalAmount.toFixed(2)}
                        </span>
                      </div>
                      
                      {/* Deposit Information */}
                      {depositInfo && depositInfo.amount > 0 && (
                        <>
                          <div className="flex justify-between text-sm sm:text-base pt-2 border-t border-white/10">
                            <span className="text-blue-200/70">Deposit Paid ({depositInfo.percentage}%):</span>
                            <span className="text-[#00CC4D]">
                              ${depositInfo.amount.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between text-xs sm:text-sm text-blue-200/50">
                            <span>Remaining Balance:</span>
                            <span>${(totalAmount - depositInfo.amount).toFixed(2)}</span>
                          </div>
                        </>
                      )}
                    </div>
                    <p className="text-xs text-right text-blue-200/70 mt-1">inc. tax</p>
                  </div>
                </>
              ) : (
                <div className="text-center py-4">
                  <p className="text-blue-200/70">
                    {error || 'No services or products selected'}
                  </p>
                </div>
              )}
            </div>

            {customThankYouMessage && (
              <div className="mt-6 sm:mt-8 bg-white/5 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-white/10 p-4 sm:p-6">
                <p className="text-sm sm:text-base text-blue-100 whitespace-pre-line text-left">
                  {customThankYouMessage}
                </p>
              </div>
            )}

            <div className="text-center mt-8 text-xs sm:text-sm text-blue-200/70">
              <p className="space-y-1 sm:space-y-0">
                <span className="block sm:inline">If you have any questions, contact {business?.name || 'the business'}</span>
                {business?.email && (
                  <span className="block sm:inline sm:ml-1">
                    at <a href={`mailto:${business.email}`} className="text-blue-400 hover:text-blue-300 transition-colors">{business.email}</a>
                    {business?.phone && (
                      <> or call at <a href={`tel:${business.phone}`} className="text-blue-400 hover:text-blue-300 transition-colors">{business.phone}</a></>
                    )}
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// Fix the missing type for the isSelectedServiceToggle state
interface ServiceToggleState {
  [id: string]: boolean;
}

async function readSupabaseFunctionErrorMessage(error: unknown): Promise<string | undefined> {
  if (!(error instanceof FunctionsHttpError) || !error.context) return undefined;
  try {
    const text = await error.context.text();
    try {
      const parsed = JSON.parse(text) as { error?: string };
      if (parsed?.error && typeof parsed.error === 'string') return parsed.error;
    } catch {
      /* not JSON */
    }
    return text || undefined;
  } catch {
    return undefined;
  }
}

interface DepositPaymentData {
  paymentId: string;
  /** Matches payments.payment_token — required to save card after pay (row still pending). */
  paymentToken: string;
  /** Mirrors payments.save_card_on_file / PayInvoice create-payment-intent body. */
  saveCardOnFile: boolean;
  saveCardCustomerId: string | null;
  depositAmount: number;
  businessId: string;
  businessName: string;
  stripeConnectAccountId: string;
  surchargeEnabled: boolean;
  surchargePercentage: number;
  proposalId: string;
  proposalTitle: string;
  offerId: string;
  offerTitle: string;
  customerEmail: string;
  customerName: string;
  depositPercentage: number;
  offerTotal: number;
}

function ProposalDepositPaymentForm({
  depositData,
  onSuccess,
  onCancel,
}: {
  depositData: DepositPaymentData;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardFunding, setCardFunding] = useState<string | null>(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [detectingCardType, setDetectingCardType] = useState(false);
  const [billingComplete, setBillingComplete] = useState(false);
  const [billingDetails, setBillingDetails] = useState<any>(null);
  const [financingLoading, setFinancingLoading] = useState(false);

  const baseAmount = depositData.depositAmount;
  const isCreditCard = cardFunding === 'credit';
  const applySurcharge = depositData.surchargeEnabled && depositData.surchargePercentage > 0 && isCreditCard;
  const surchargeAmount = applySurcharge ? Math.round(baseAmount * (depositData.surchargePercentage / 100) * 100) / 100 : 0;
  const totalAmount = Math.round((baseAmount + surchargeAmount) * 100) / 100;

  useEffect(() => {
    if (!cardComplete || !depositData.surchargeEnabled || depositData.surchargePercentage <= 0 || !stripe || !elements) {
      if (!cardComplete) setCardFunding(null);
      return;
    }

    const detectCardType = async () => {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) return;

      setDetectingCardType(true);
      try {
        const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
        });

        if (pmError) {
          setCardFunding(null);
        } else if (paymentMethod) {
          setCardFunding(paymentMethod.card?.funding || null);
        }
      } catch (err) {
        setCardFunding(null);
      } finally {
        setDetectingCardType(false);
      }
    };

    detectCardType();
  }, [cardComplete, depositData.surchargeEnabled, depositData.surchargePercentage, stripe, elements]);

  const handleCardChange = (event: any) => {
    if (!event.complete) setCardFunding(null);
    setCardComplete(event.complete ?? false);
    setError(event.error ? event.error.message : null);
  };

  const handleFinancingClick = async () => {
    setFinancingLoading(true);
    setError(null);
    try {
      const financingTotal = Math.round(baseAmount * 100);
      const currentUrl = window.location.href.split('?')[0];
      const successUrl = `${currentUrl}?deposit_financing_success=true&offer_id=${depositData.offerId}&payment_id=${depositData.paymentId}`;
      const cancelUrl = currentUrl;

      const { data, error: fnError } = await supabase.functions.invoke('create-platform-payment', {
        body: {
          businessId: depositData.businessId,
          totalAmount: financingTotal,
          successUrl,
          cancelUrl,
          description: `Deposit for Proposal: ${depositData.proposalTitle}`,
          customerEmail: depositData.customerEmail,
          customerName: depositData.customerName,
          paymentMethodTypes: ['klarna', 'afterpay_clearpay', 'affirm'],
          paymentId: depositData.paymentId,
          metadata: {
            source: 'proposal_deposit_financing',
            payment_id: depositData.paymentId,
            proposal_id: depositData.proposalId,
            offer_id: depositData.offerId,
            payment_type: 'deposit',
            base_amount: String(baseAmount),
            surcharge_amount: '0',
            total_amount: String(baseAmount),
            card_funding: 'financing',
          }
        }
      });

      if (fnError || !data?.checkoutUrl) {
        setError('Unable to load financing options. The business may not have financing enabled.');
        return;
      }

      window.location.href = data.checkoutUrl;
    } catch (err) {
      console.error('Financing error:', err);
      setError('Failed to load financing options. Please try again.');
    } finally {
      setFinancingLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      setError('Payment system not loaded. Please refresh.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Card element not found. Please refresh.');
      return;
    }

    if (!billingComplete || !billingDetails) {
      setError('Please complete your billing information.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    let finalFunding = cardFunding;
    let finalSurcharge = surchargeAmount;
    let finalTotal = totalAmount;

    if (depositData.surchargeEnabled && depositData.surchargePercentage > 0 && !cardFunding) {
      try {
        const { paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
        });
        if (paymentMethod?.card?.funding) {
          finalFunding = paymentMethod.card.funding;
          if (finalFunding === 'credit') {
            finalSurcharge = Math.round(baseAmount * (depositData.surchargePercentage / 100) * 100) / 100;
            finalTotal = Math.round((baseAmount + finalSurcharge) * 100) / 100;
          }
        }
      } catch (err) {
        console.error('Card type detection at submit failed:', err);
      }
    }

    try {
      const { data: intentData, error: intentError } = await supabase.functions.invoke(
        'create-payment-intent',
        {
          body: {
            businessId: depositData.businessId,
            amount: finalTotal,
            baseAmount: baseAmount,
            surchargeAmount: finalSurcharge,
            paymentId: depositData.paymentId,
            description: `Deposit for Proposal: ${depositData.proposalTitle}`,
            metadata: {
              source: 'proposal_deposit',
              payment_id: depositData.paymentId,
              proposal_id: depositData.proposalId,
              offer_id: depositData.offerId,
              payment_type: 'deposit',
              base_amount: String(baseAmount),
              surcharge_amount: String(finalSurcharge),
              total_amount: String(finalTotal),
              card_funding: finalFunding || 'unknown',
            },
            ...(depositData.saveCardOnFile && depositData.saveCardCustomerId
              ? { saveCardForCustomerId: depositData.saveCardCustomerId }
              : {}),
          }
        }
      );

      if (intentError) {
        const serverMsg = await readSupabaseFunctionErrorMessage(intentError);
        throw new Error(
          serverMsg || intentError.message || 'Failed to create payment. Please try again.',
        );
      }

      const responseData = intentData?.data || intentData;
      if (!responseData?.clientSecret) {
        throw new Error('Payment setup failed. Please try again.');
      }

      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        responseData.clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: billingDetails?.name || depositData.customerName || undefined,
              email: depositData.customerEmail || undefined,
              address: billingDetails?.address || undefined,
            }
          }
        }
      );

      if (confirmError) {
        throw new Error(confirmError.message || 'Payment failed. Please check your card details.');
      }

      if (paymentIntent?.status === 'succeeded') {
        // Save card while payment row is still pending (edge function requires pending).
        if (paymentIntent.id && depositData.paymentToken) {
          try {
            const { data: saveRaw, error: saveErr } = await supabase.functions.invoke(
              'save-customer-card-after-online-payment',
              { body: { paymentToken: depositData.paymentToken, paymentIntentId: paymentIntent.id } },
            );
            const savePayload =
              saveRaw && typeof saveRaw === 'object' && 'data' in saveRaw && saveRaw.data
                ? saveRaw.data
                : saveRaw;
            if (saveErr) {
              const m = await readSupabaseFunctionErrorMessage(saveErr);
              console.error('Save card (proposal deposit):', m || saveErr.message);
              toast.error(
                m ||
                  saveErr.message ||
                  'Deposit paid, but the card could not be saved. Add it from the customer Wallet in the app.',
              );
            } else if (
              savePayload &&
              typeof savePayload === 'object' &&
              'error' in savePayload &&
              typeof (savePayload as { error?: string }).error === 'string'
            ) {
              const em = (savePayload as { error: string }).error;
              console.error('Save card (proposal deposit):', em);
              toast.error(
                `${em} You can add a card from the customer Wallet.`,
              );
            } else if (
              depositData.saveCardOnFile &&
              savePayload &&
              typeof savePayload === 'object' &&
              (savePayload as { skipped?: boolean }).skipped
            ) {
              toast(
                'Deposit received. Card was not saved — save-on-file was not active on this payment. You can add a card from the customer Wallet.',
                { icon: 'ℹ️', duration: 6000 },
              );
            }
          } catch (e) {
            console.error('Save card (proposal deposit):', e);
            toast.error(
              'Deposit paid, but saving the card failed. Add it from the customer Wallet in the app.',
            );
          }
        }

        const { error: updateError } = await supabase
          .from('payments')
          .update({
            status: 'completed',
            amount: finalTotal,
            base_amount: baseAmount,
            surcharge_amount: finalSurcharge,
            tip_amount: 0,
            stripe_payment_intent_id: paymentIntent.id,
            method: 'credit_card',
            notes: finalSurcharge > 0
              ? `Deposit payment (includes $${finalSurcharge.toFixed(2)} processing fee)`
              : 'Deposit payment via Stripe'
          })
          .eq('id', depositData.paymentId);

        if (updateError) {
          console.error('Failed to update payment record:', updateError);
        }

        onSuccess();
      } else {
        throw new Error('Payment was not completed. Please try again.');
      }
    } catch (err: any) {
      console.error('Payment error:', err);
      setError(err.message || 'An unexpected error occurred. Please try again.');
      setIsProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#374151',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        '::placeholder': { color: '#9CA3AF' },
      },
      invalid: { color: '#EF4444', iconColor: '#EF4444' },
    },
    hidePostalCode: true,
    disableLink: true,
  };

  return (
    <>
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Deposit Amount</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
          <input
            type="text"
            value={depositData.depositAmount.toFixed(2)}
            readOnly
            className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-lg font-medium text-gray-900 cursor-not-allowed"
          />
        </div>
        <p className="mt-2 text-sm text-gray-500">
          {depositData.depositPercentage}% deposit on ${depositData.offerTotal.toFixed(2)} total
        </p>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Billing Information</label>
        <AddressElement
          options={{ mode: 'billing' }}
          onChange={(event) => {
            setBillingComplete(event.complete);
            if (event.complete) {
              setBillingDetails(event.value);
            }
          }}
        />
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Card Details</label>
        <div className="px-4 py-3 bg-white border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all">
          <CardElement options={cardElementOptions} onChange={handleCardChange} />
        </div>
        {error && (
          <div className="mt-2 flex items-center text-sm text-red-600">
            <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>

      {cardComplete && depositData.surchargeEnabled && depositData.surchargePercentage > 0 && (
        <div className="mb-4">
          {detectingCardType ? (
            <div className="flex items-center text-sm text-gray-400 py-1">
              <div className="w-3 h-3 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin mr-2"></div>
              Detecting card type...
            </div>
          ) : applySurcharge ? (
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              A {depositData.surchargePercentage}% card processing fee applies to credit card payments.
            </div>
          ) : cardFunding === 'debit' ? (
            <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              Debit card detected — no processing fee.
            </div>
          ) : null}
        </div>
      )}

      <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="font-medium text-gray-900 mb-3 text-sm">Payment Summary</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Deposit Amount</span>
            <span className="font-medium text-gray-900">${baseAmount.toFixed(2)}</span>
          </div>
          {surchargeAmount > 0 && (
            <div className="flex justify-between">
              <span className="text-amber-700">Card Processing Fee ({depositData.surchargePercentage}%)</span>
              <span className="font-medium text-amber-700">${surchargeAmount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between border-t border-gray-200 pt-2 mt-2">
            <span className="text-gray-900 font-semibold">Total</span>
            <span className="font-semibold text-gray-900">${totalAmount.toFixed(2)}</span>
          </div>
        </div>
      </div>

      <button
        onClick={handleSubmit}
        disabled={isProcessing || !stripe || !cardComplete || !billingComplete}
        className="w-full bg-blue-600 text-white py-4 px-6 rounded-lg font-medium text-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
      >
        {isProcessing ? (
          <div className="flex items-center justify-center">
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
            Processing...
          </div>
        ) : (
          `Pay Deposit $${totalAmount.toFixed(2)}`
        )}
      </button>

      {/* Financing Option */}
      <div className="mt-4 relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-3 bg-white text-gray-500">or</span>
        </div>
      </div>

      <button
        onClick={handleFinancingClick}
        disabled={financingLoading || isProcessing}
        className="mt-4 w-full bg-white text-gray-700 py-3 px-6 rounded-lg font-medium text-sm border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
      >
        {financingLoading ? (
          <div className="flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin mr-2"></div>
            Loading options...
          </div>
        ) : (
          <span className="flex items-center justify-center gap-2">
            View Financing Options
            <span className="text-xs text-gray-400">(Klarna, Afterpay, Affirm)</span>
          </span>
        )}
      </button>

      <div className="mt-4 flex justify-center">
        <button
          onClick={onCancel}
          disabled={isProcessing}
          className="text-sm text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
      </div>

      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="flex items-center justify-center space-x-6 text-xs text-gray-500">
          <div className="flex items-center">
            <Shield className="w-3.5 h-3.5 mr-1.5" />
            <span>Stripe Secure</span>
          </div>
          <div className="flex items-center">
            <Lock className="w-3.5 h-3.5 mr-1.5" />
            <span>256-bit SSL</span>
          </div>
          <div className="flex items-center">
            <CreditCard className="w-3.5 h-3.5 mr-1.5" />
            <span>All Cards Accepted</span>
          </div>
        </div>
      </div>
    </>
  );
}

// Update the PublicProposal component
const PublicProposal: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [expandedOffers, setExpandedOffers] = useState<string[]>([]);
  const [publicProposalView, setPublicProposalView] = useState<'standard' | 'partial' | 'open'>('standard');
  const [googleReviewsBadge, setGoogleReviewsBadge] = useState<{
    enabled: boolean;
    rating: number | null;
    count: number | null;
    url: string | null;
  }>({ enabled: false, rating: null, count: null, url: null });
  const [customPublicMessage, setCustomPublicMessage] = useState<string | null>(null);
  const [logoOnly, setLogoOnly] = useState(false);
  const [logoSize, setLogoSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [selectedOffer, setSelectedOffer] = useState<string | null>(null);
  const [selectedOptionalServices, setSelectedOptionalServices] = useState<Set<string>>(new Set());
  const [directlyFetchedServices, setDirectlyFetchedServices] = useState<any[]>([]);
  const [isSelectedServiceToggle, setIsSelectedServiceToggle] = useState<ServiceToggleState>({});
  const [selectingOffer, setSelectingOffer] = useState<string | null>(null); // For button loading state
  const [showDeclineModal, setShowDeclineModal] = useState<boolean>(false);
  const [declineNote, setDeclineNote] = useState<string>('');
  const [isDeclining, setIsDeclining] = useState<boolean>(false);
  const [productPhotos, setProductPhotos] = useState<Record<string, string>>({});
  const [servicePhotos, setServicePhotos] = useState<Record<string, string[]>>({});
  const [offerDeposits, setOfferDeposits] = useState<Record<string, number>>({});
  /** Per offer: staff enabled "save card on file" for online deposit (proposal_offers.save_card_on_deposit). */
  const [offerSaveCardOnDeposit, setOfferSaveCardOnDeposit] = useState<Record<string, boolean>>({});
  /** Per offer: show customer address below the offer title on the public page. */
  const [offerShowCustomerAddress, setOfferShowCustomerAddress] = useState<Record<string, boolean>>({});
  const [depositPaymentData, setDepositPaymentData] = useState<DepositPaymentData | null>(null);
  const [showDepositPayment, setShowDepositPayment] = useState(false);
  const [depositStripePromise, setDepositStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [publicBusinessTaxRates, setPublicBusinessTaxRates] = useState<{ name: string; rate: number }[]>([]);
  const [publicServiceAddlTaxes, setPublicServiceAddlTaxes] = useState<{ service_id: string; name: string; rate: number }[]>([]);

  useEffect(() => {
    if (business?.id) {
      supabase
        .from('tax_rates')
        .select('name, rate')
        .eq('business_id', business.id)
        .then(({ data }) => { if (data) setPublicBusinessTaxRates(data); });
    }
  }, [business?.id]);

  useEffect(() => {
    if (!proposal?.services) return;
    const svcIds = [...new Set(proposal.services.map(s => s.id).filter(Boolean))];
    if (svcIds.length === 0) { setPublicServiceAddlTaxes([]); return; }
    supabase
      .from('service_additional_taxes')
      .select('service_id, name, rate')
      .in('service_id', svcIds)
      .then(({ data }) => { if (data) setPublicServiceAddlTaxes(data); });
  }, [proposal?.services]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('deposit_financing_success') === 'true' && token) {
      const offerId = params.get('offer_id');
      const paymentId = params.get('payment_id');
      window.history.replaceState({}, '', window.location.pathname);

      const handleFinancingReturn = async () => {
        if (!offerId || !paymentId) return;

        let paid = false;
        for (let i = 0; i < 10; i++) {
          const { data: paymentCheck } = await supabase
            .from('payments')
            .select('status')
            .eq('id', paymentId)
            .single();

          if (paymentCheck?.status === 'completed' || paymentCheck?.status === 'succeeded' || paymentCheck?.status === 'paid') {
            paid = true;
            break;
          }
          await new Promise(r => setTimeout(r, 2000));
        }

        if (!paid) {
          await supabase
            .from('payments')
            .update({ status: 'completed', method: 'financing' })
            .eq('id', paymentId);
        }

        const { data: result } = await supabase.rpc('get_proposal_data_fast', { p_token: token });
        if (result?.success && result.proposal) {
          const offerServices = result.proposal.services?.filter((s: any) => s.offer?.id === offerId) || [];
          await supabase.rpc('approve_proposal_offer_fast', {
            p_proposal_id: result.proposal.id,
            p_offer_id: offerId,
            p_business_id: result.proposal.business_id,
            p_customer_id: result.proposal.customer_id,
            p_proposal_title: result.proposal.title,
            p_offer_title: offerServices[0]?.offer?.title || 'Selected Offer'
          });

          const { data: refreshed } = await supabase.rpc('get_proposal_data_fast', { p_token: token });
          if (refreshed?.success) {
            setProposal(refreshed.proposal);
            const businessWithLogo = {
              ...refreshed.business,
              logo_url: (refreshed.business as any).booking_settings?.logo_url || refreshed.business.logo_url
            };
            setBusiness(businessWithLogo);
            setSelectedOffer(offerId);
          }
        }
        setIsLoading(false);
      };

      handleFinancingReturn();
    }
  }, [token]);

  // Helper function to directly update a service selection state
  const updateServiceSelection = async (serviceId: string, isSelected: boolean) => {
    if (!supabase) return false;
    
    try {
      const { error } = await supabase
        .from('proposal_services')
        .update({ is_selected: isSelected })
        .eq('id', serviceId);
        
      return !error;
    } catch (err) {
      console.error(`Error updating service ${serviceId}:`, err);
      return false;
    }
  };

  // Update the handleToggleSelectableService function with proper types
  const handleToggleSelectableService = (serviceId: string) => {
    setIsSelectedServiceToggle(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
    
    // Update the services array with the new toggle state
    setProposal(prevProposal => {
      if (!prevProposal) return prevProposal;
      
      const updatedServices = prevProposal.services.map(service => {
        if (service.id === serviceId) {
          return {
            ...service,
            is_selected: !isSelectedServiceToggle[serviceId]
          };
        }
        return service;
      });
      
      return {
        ...prevProposal,
        services: updatedServices
      };
    });
  };

  // Fix any remaining typing issues in the renderService function
  const renderService = (service: Service) => {
    // ... existing code ...
  };
  
  // Update the handleAcceptOffer function with proper types
  const handleAcceptOffer = async (offerId: string) => {
    // ... existing code ...
  };

  // Add a function to ensure optional services are properly initialized
  const resetOptionalServiceSelections = async (services: Service[]) => {
    if (!supabase) return;
    
    // For each optional service, ensure it's initially not selected in the database
    for (const service of services) {
      if (!service.isSelectable || !service.id || service.is_selected === false) continue;
      
      // If it's an optional service and not explicitly set to false in the database,
      // make sure it's set to false
      console.log(`Resetting optional service ${service.id} to unselected state`);
      
      try {
        const { error } = await supabase.rpc('update_service_selection', {
          p_service_id: service.id,
          p_is_selected: false
        });
        
        if (error) {
          console.error(`Error resetting service ${service.id}:`, error);
        }
      } catch (err) {
        console.error(`Error calling service reset:`, err);
      }
    }
  };

  const fetchProposalData = async () => {
    try {
      console.log('🚀 Starting FAST proposal data fetch with token:', token);
      
      if (!token) {
        console.error('❌ No token provided in URL');
        setError('No proposal token provided');
        setIsLoading(false);
        return;
      }

      if (!supabase) {
        console.error('❌ Supabase client is not initialized');
        setError('Database connection is not available. Please refresh the page.');
        setIsLoading(false);
        return;
      }

      // **SINGLE OPTIMIZED RPC CALL** - Replaces 4+ sequential queries
      console.log('📡 Fetching ALL data in single optimized call...');
      const startTime = Date.now();
      
      const { data: result, error: rpcError } = await supabase
        .rpc('get_proposal_data_fast', { p_token: token });

      const loadTime = Date.now() - startTime;
      console.log(`⚡ Database call completed in ${loadTime}ms`);

      if (rpcError) {
        console.error('RPC Error:', rpcError);
        setError('Failed to load proposal data');
        setIsLoading(false);
        return;
      }

      if (!result) {
        console.error('No result from RPC');
        setError('No data returned from server');
        setIsLoading(false);
        return;
      }

      // Handle different error cases
      if (!result.success) {
        switch (result.error) {
          case 'INVALID_TOKEN':
            setError('Invalid proposal token - link not found');
            break;
          case 'EXPIRED':
        setError('Proposal link has expired');
        setExpired(true);
            break;
          case 'DATABASE_ERROR':
            setError(`Database error: ${result.message}`);
            break;
          default:
            setError(result.message || 'Unknown error occurred');
        }
        setIsLoading(false);
        return;
      }
      
      // **INSTANT DATA PROCESSING** - Data is already optimized from database
      const proposalData = result.proposal;
      const businessData = result.business;

      // Validate that we have the required data
      if (!proposalData || !businessData) {
        console.error('❌ Missing required data from RPC:', {
          hasProposal: !!proposalData,
          hasBusiness: !!businessData,
          result: result
        });
        setError('Failed to load proposal data - missing information');
        setIsLoading(false);
        return;
      }

      console.log('✅ Proposal data loaded instantly:', proposalData);
      console.log('✅ Services loaded:', proposalData.services?.length || 0);

      // If the proposal is approved or booked, find and set the selected offer
      if (proposalData.status === 'approved' || proposalData.status === 'booked') {
        const approvedService = proposalData.services?.find((service: any) => service.offer?.status === 'approved');
        if (approvedService?.offer?.id) {
          setSelectedOffer(approvedService.offer.id);
          console.log(`Found approved offer: ${approvedService.offer.id}`);
        } else {
          // Fallback: check proposal notes for the approved_offer_id
          try {
            const notes = typeof proposalData.notes === 'string' ? JSON.parse(proposalData.notes) : proposalData.notes;
            if (notes?.approved_offer_id) {
              setSelectedOffer(notes.approved_offer_id);
              console.log(`Found approved offer from notes: ${notes.approved_offer_id}`);
            }
          } catch (e) {
            console.warn('Could not parse proposal notes for approved offer ID');
          }
        }
      }

      // Fetch discount information for all services
      console.log('🔍 Fetching discount information for services...');
      const serviceIds = proposalData.services?.map((service: any) => service.id).filter(Boolean) || [];
      
      if (serviceIds.length > 0) {
        const { data: discountData, error: discountError } = await supabase
          .from('proposal_services')
          .select('id, discount_amount, discount_type, discount_value, compare_at_price, line_key')
          .in('id', serviceIds);
          
        if (!discountError && discountData) {
          // Merge discount data + line_key with existing services
          const byId = new Map<string, any>(discountData.map((d: any) => [d.id, d]));
          const servicesWithDiscounts = proposalData.services?.map((service: any) => {
            const info = byId.get(service.id);
            if (info) {
              return {
                ...service,
                discount_amount: info.discount_amount || 0,
                discount_type: info.discount_type,
                discount_value: info.discount_value,
                compare_at_price: info.compare_at_price ?? null,
                line_key: info.line_key,
              };
            }
            return service;
          });
          
          proposalData.services = servicesWithDiscounts;
          console.log('✅ Discount data merged with services');
        } else {
          console.warn('Failed to fetch discount data:', discountError);
        }
      }

      // Fetch products for this proposal
      console.log('🔍 Fetching products for proposal...');
      const { data: productsData, error: productsError } = await supabase
        .from('proposal_products_with_details')
        .select('*')
        .eq('proposal_id', proposalData.id);
        
      if (!productsError && productsData) {
        // Group products by offer
        const productsByOffer: Record<string, any[]> = {};
        productsData.forEach((product: any) => {
          if (!productsByOffer[product.offer_id]) {
            productsByOffer[product.offer_id] = [];
          }
          productsByOffer[product.offer_id].push({
            id: product.id,
            name: product.name,
            price: product.price,
            quantity: product.quantity,
            category_name: product.stock_category_name || product.category_name,
            stock_item_id: product.stock_item_id,
            discount_amount: product.discount_amount || 0,
            discount_type: product.discount_type,
            discount_value: product.discount_value,
            offer: {
              id: product.offer_id,
              title: '', // Will be filled from services
              status: ''
            }
          });
        });
        
        // Add products to proposal data
        proposalData.products = Object.values(productsByOffer).flat();
        console.log('✅ Products loaded:', proposalData.products?.length || 0);
        
        // Load product photos
        const stockItemIds = proposalData.products
          .map((product: any) => product.stock_item_id)
          .filter(Boolean);
          
        if (stockItemIds.length > 0) {
          try {
            const photoMap = await ProductPhotoService.getPhotosByStockItemIds(stockItemIds);
            const photoUrls: Record<string, string> = {};
            
            if (photoMap && typeof photoMap === 'object') {
              Object.entries(photoMap).forEach(([itemId, photo]) => {
                if (photo && photo.photo_url) {
                  photoUrls[itemId] = photo.photo_url;
                }
              });
            }
            
            setProductPhotos(photoUrls);
            console.log('✅ Product photos loaded:', Object.keys(photoUrls).length);
          } catch (photoError) {
            console.error('Error loading product photos:', photoError);
            setProductPhotos({});
          }
        }
      } else {
        console.warn('Failed to fetch products data:', productsError);
        proposalData.products = [];
      }

      // Load per-service photos, grouped by the service's stable line_key.
      try {
        const { data: spData } = await supabase
          .from('proposal_service_photos')
          .select('line_key, file_path, order_index')
          .eq('proposal_id', proposalData.id)
          .order('order_index', { ascending: true });
        const photoByKey: Record<string, string[]> = {};
        for (const row of (spData || [])) {
          if (!row.line_key || !row.file_path) continue;
          const { data: pub } = supabase.storage.from('user-uploads').getPublicUrl(row.file_path);
          (photoByKey[row.line_key] ||= []).push(pub.publicUrl);
        }
        setServicePhotos(photoByKey);
        console.log('✅ Service photos loaded for', Object.keys(photoByKey).length, 'line(s)');
      } catch (e) {
        console.error('Error loading per-service photos:', e);
        setServicePhotos({});
      }

      // Fetch deposit percentages for all offers
      console.log('🔍 Fetching deposit percentages for offers...');
      const offerIds = proposalData.services?.reduce((ids: string[], service: any) => {
        if (service.offer?.id && !ids.includes(service.offer.id)) {
          ids.push(service.offer.id);
        }
        return ids;
      }, [] as string[]) || [];
      
      if (offerIds.length > 0) {
        const { data: offersData, error: offersError } = await supabase
          .from('proposal_offers')
          .select('id, deposit_percentage, save_card_on_deposit, show_customer_address')
          .in('id', offerIds);
          
        if (!offersError && offersData) {
          const deposits: Record<string, number> = {};
          const saveCard: Record<string, boolean> = {};
          const showAddress: Record<string, boolean> = {};
          offersData.forEach((offer: any) => {
            if (offer.deposit_percentage && offer.deposit_percentage > 0) {
              deposits[offer.id] = offer.deposit_percentage;
            }
            if (offer.save_card_on_deposit === true) {
              saveCard[offer.id] = true;
            }
            if (offer.show_customer_address === true) {
              showAddress[offer.id] = true;
            }
          });
          setOfferDeposits(deposits);
          setOfferSaveCardOnDeposit(saveCard);
          setOfferShowCustomerAddress(showAddress);
          console.log('✅ Deposit percentages loaded:', Object.keys(deposits).length);
        } else {
          console.warn('Failed to fetch deposit percentages:', offersError);
        }
      }

      // Validate proposal and business data before setting state
      if (!proposalData.id || !businessData.id) {
        console.error('❌ Invalid proposal or business data:', {
          proposalId: proposalData?.id,
          businessId: businessData?.id
        });
        setError('Invalid proposal data received from server');
        setIsLoading(false);
        return;
      }

      // Set all data at once
      setProposal(proposalData);
      
      // Extract logo_url from booking_settings if available
      const businessWithLogo = {
        ...businessData,
        logo_url: (businessData as any).booking_settings?.logo_url || businessData.logo_url
      };
      setBusiness(businessWithLogo);

      // Load the "Public Proposal View" setting (standard | partial | open).
      let viewMode: 'standard' | 'partial' | 'open' = 'standard';
      try {
        const { data: viewData } = await supabase.rpc('get_proposal_view_setting', {
          p_business_id: businessData.id,
        });
        if (viewData === 'partial' || viewData === 'open') viewMode = viewData;
      } catch (viewErr) {
        console.warn('Could not load public proposal view setting, defaulting to standard:', viewErr);
      }
      setPublicProposalView(viewMode);

      try {
        const { data: badgeData } = await supabase.rpc('get_proposal_google_reviews_badge', {
          p_business_id: businessData.id,
        });
        if (badgeData) {
          const enabled = badgeData.enabled === true || badgeData.enabled === 'true';
          const parsedRating = badgeData.rating != null && badgeData.rating !== ''
            ? Number(badgeData.rating)
            : null;
          setGoogleReviewsBadge({
            enabled,
            // Default to 5 when enabled but rating was never saved (toggle-only).
            rating: parsedRating != null && !Number.isNaN(parsedRating)
              ? parsedRating
              : (enabled ? 5 : null),
            count: badgeData.count != null && badgeData.count !== '' ? Number(badgeData.count) : null,
            url: typeof badgeData.url === 'string' && badgeData.url.trim() ? badgeData.url.trim() : null,
          });
        }
      } catch (badgeErr) {
        console.warn('Could not load proposal Google reviews badge:', badgeErr);
      }

      try {
        const { data: msgData } = await supabase.rpc('get_proposal_public_message', {
          p_business_id: businessData.id,
        });
        if (msgData) {
          const enabled = msgData.enabled === true || msgData.enabled === 'true';
          const message = typeof msgData.message === 'string' ? msgData.message.trim() : '';
          setCustomPublicMessage(enabled && message ? message : null);
        }
      } catch (msgErr) {
        console.warn('Could not load proposal public message:', msgErr);
      }

      try {
        const { data: logoData } = await supabase.rpc('get_proposal_logo_setting', {
          p_business_id: businessData.id,
        });
        if (logoData) {
          setLogoOnly(logoData.logo_only === true || logoData.logo_only === 'true');
          if (logoData.logo_size === 'small' || logoData.logo_size === 'medium' || logoData.logo_size === 'large') {
            setLogoSize(logoData.logo_size);
          }
        }
      } catch (logoErr) {
        console.warn('Could not load proposal logo setting:', logoErr);
      }

      if (viewMode === 'open') {
        // Expand every offer by default.
        const offerIds = Array.from(
          new Set((proposalData.services || []).map((s: any) => s.offer?.id).filter(Boolean))
        ) as string[];
        setExpandedOffers(offerIds);
      } else {
        // Standard & partial both start collapsed (partial shows a snippet).
        setExpandedOffers([]);
      }
      setIsLoading(false);

      console.log(`🎉 TOTAL LOAD TIME: ${Date.now() - startTime}ms (target: <1000ms)`);
      
    } catch (err) {
      console.error('❌ Error in fetchProposalData:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('❌ Full error details:', {
        error: err,
        message: errorMessage,
        stack: err instanceof Error ? err.stack : undefined,
        token: token
      });
      setError(`Failed to load proposal: ${errorMessage}`);
      setIsLoading(false);
    } finally {
      // Ensure loading is always set to false, even if there's an unexpected error
      setIsLoading(false);
    }
  };

  const handleToggleOffer = (offerId: string) => {
    setExpandedOffers(prev =>
      prev.includes(offerId)
        ? prev.filter(id => id !== offerId)
        : [...prev, offerId]
    );
  };

  const handleSelectOffer = async (offerId: string) => {
    if (!proposal || !supabase) {
      console.error("Cannot select offer: supabase client or proposal is null");
      return;
    }

    try {
      // Show loading state on button
      setSelectingOffer(offerId);
      
      // Get all services for this offer
      const offerServices = proposal.services.filter(service => 
        service.offer?.id === offerId
      );
      
      // Get products for this offer
      const offerProducts = proposal.products?.filter(product => 
        product.offer?.id === offerId
      ) || [];
      
      console.log(`🚀 Processing offer: ${offerId} with ${offerServices.length} services and ${offerProducts.length} products`);
      
      // Check if deposit is required
      const depositPercentage = offerDeposits[offerId];
      
      if (depositPercentage && depositPercentage > 0) {
        // Calculate deposit amount based on selected services/products
        const serviceSubtotal = offerServices.reduce((total, service) => {
          const isOptionalByName = service.name?.includes('(Optional)') || false;
          let isSelectable = Boolean(service.isSelectable);
          
          if (isOptionalByName && !isSelectable) {
            isSelectable = true;
          }
          
          // Check if service is selected (from database state)
          const isSelected = service.is_selected === true;
          const includeInTotal = !isSelectable || (isSelectable && isSelected);
          
          if (!includeInTotal) {
            return total;
          }
          
          const price = typeof service.price === 'number' ? service.price : 0;
          const quantity = typeof service.quantity === 'number' ? service.quantity : 1;
          return total + (price * quantity);
        }, 0);

        const productSubtotal = offerProducts.reduce((total, product) => {
          const price = typeof product.price === 'number' ? product.price : 0;
          const quantity = typeof product.quantity === 'number' ? product.quantity : 1;
          return total + (price * quantity);
        }, 0);

        const subtotal = serviceSubtotal + productSubtotal;

        // Calculate discounts
        const serviceDiscountAmount = offerServices.reduce((totalDiscount, service) => {
          const isOptionalByName = service.name?.includes('(Optional)') || false;
          let isSelectable = Boolean(service.isSelectable);
          
          if (isOptionalByName && !isSelectable) {
            isSelectable = true;
          }
          
          // Check if service is selected (from database state)
          const isSelected = service.is_selected === true;
          const includeInTotal = !isSelectable || (isSelectable && isSelected);
          
          if (!includeInTotal) {
            return totalDiscount;
          }
          
          const discountAmount = typeof (service as any).discount_amount === 'number' ? (service as any).discount_amount : 0;
          return totalDiscount + discountAmount;
        }, 0);

        const productDiscountAmount = offerProducts.reduce((totalDiscount, product) => {
          const discountAmount = typeof product.discount_amount === 'number' ? product.discount_amount : 0;
          return totalDiscount + discountAmount;
        }, 0);

        const totalDiscountAmount = serviceDiscountAmount + productDiscountAmount;
        
        const combinedRate = publicBusinessTaxRates.reduce((sum, r) => sum + r.rate, 0) / 100;
        const discountedSubtotal = subtotal - totalDiscountAmount;
        const bizTax = Math.round((discountedSubtotal * combinedRate) * 100) / 100;
        let depAddlTax = 0;
        for (const svc of offerServices) {
          const svcId = (svc as any).service_id || svc.id;
          if (!svcId) continue;
          const svcNet = (svc.price || 0) - ((svc as any).discount_amount || 0);
          for (const atx of publicServiceAddlTaxes.filter(t => t.service_id === svcId)) {
            depAddlTax += svcNet * atx.rate / 100;
          }
        }
        depAddlTax = Math.round(depAddlTax * 100) / 100;
        const total = Math.round((discountedSubtotal + bizTax + depAddlTax) * 100) / 100;
        
        // Calculate deposit amount
        const depositAmount = Math.round((total * depositPercentage / 100) * 100) / 100;
        
        console.log(`💰 Deposit required: ${depositPercentage}% = $${depositAmount} (from total: $${total})`);

        const saveCardRequested = !!offerSaveCardOnDeposit[offerId];
        const crmCustomerId = proposal.customer_id || null;
        const paymentToken = crypto.randomUUID();
        
        // Remove any stale pending deposit payments for this proposal before creating a new one
        await supabase
          .from('payments')
          .delete()
          .eq('proposal_id', proposal.id)
          .eq('payment_type', 'deposit')
          .eq('status', 'pending');
        
        // Create payment record with pending status
        const { data: payment, error: paymentError } = await supabase
          .from('payments')
          .insert({
            business_id: proposal.business_id,
            proposal_id: proposal.id,
            amount: depositAmount,
            method: 'credit_card',
            status: 'pending',
            payment_type: 'deposit',
            payment_token: paymentToken,
            save_card_on_file: !!(saveCardRequested && crmCustomerId),
            save_card_customer_id: saveCardRequested && crmCustomerId ? crmCustomerId : null,
            notes: `Deposit for proposal offer: ${offerServices[0]?.offer?.title || 'Selected Offer'}`
          })
          .select()
          .single();
        
        if (paymentError || !payment) {
          console.error('❌ Error creating payment record:', paymentError);
          setSelectingOffer(null);
          alert('Failed to create payment record. Please try again.');
          return;
        }
        
        console.log('✅ Payment record created:', payment.id);
        
        // Get business info for Stripe (including surcharge settings)
        const { data: businessData, error: businessError } = await supabase
          .from('businesses')
          .select('stripe_connect_account_id, stripe_connect_charges_enabled, online_payments_enabled, name, credit_card_surcharge_enabled, credit_card_surcharge_percentage')
          .eq('id', proposal.business_id)
          .single();
        
        if (businessError || !businessData) {
          console.error('❌ Error fetching business data:', businessError);
          setSelectingOffer(null);
          alert('Failed to fetch business payment information. Please try again.');
          return;
        }
        
        if (!businessData.stripe_connect_account_id || !businessData.stripe_connect_charges_enabled || !businessData.online_payments_enabled) {
          console.error('❌ Business does not have Stripe Connect properly configured');
          setSelectingOffer(null);
          alert('Online payments are not available for this business. Please contact them directly.');
          return;
        }
        
        // Set up deposit payment data for embedded card form
        setDepositPaymentData({
          paymentId: payment.id,
          paymentToken,
          saveCardOnFile: !!(saveCardRequested && crmCustomerId),
          saveCardCustomerId: saveCardRequested && crmCustomerId ? crmCustomerId : null,
          depositAmount,
          businessId: proposal.business_id,
          businessName: businessData.name,
          stripeConnectAccountId: businessData.stripe_connect_account_id,
          surchargeEnabled: businessData.credit_card_surcharge_enabled ?? false,
          surchargePercentage: businessData.credit_card_surcharge_percentage ?? 0,
          proposalId: proposal.id,
          proposalTitle: proposal.title,
          offerId,
          offerTitle: offerServices[0]?.offer?.title || 'Selected Offer',
          customerEmail: proposal.customer?.email || 'customer@example.com',
          customerName: `${proposal.customer?.first_name || ''} ${proposal.customer?.last_name || ''}`.trim() || 'Customer',
          depositPercentage,
          offerTotal: total,
        });
        
        // Initialize Stripe for embedded payment
        try {
          const platformKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
          const { data: keyData, error: keyError } = await supabase.functions.invoke(
            'get-stripe-connect-publishable-key',
            { body: { businessId: proposal.business_id, platformPublishableKey: platformKey } }
          );
          
          if (!keyError && keyData?.publishableKey && keyData?.accountId) {
            const stripeInstance = await loadStripe(keyData.publishableKey, {
              stripeAccount: keyData.accountId
            } as any);
            setDepositStripePromise(Promise.resolve(stripeInstance));
          } else {
            console.error('❌ Failed to get Stripe publishable key:', keyError);
            setSelectingOffer(null);
            alert('Failed to initialize payment system. Please try again.');
            return;
          }
        } catch (stripeInitErr) {
          console.error('❌ Stripe initialization error:', stripeInitErr);
          setSelectingOffer(null);
          alert('Failed to initialize payment system. Please try again.');
          return;
        }
        
        setShowDepositPayment(true);
        setSelectingOffer(null);
        
        return; // Exit early - don't approve offer yet
      }
      
      // No deposit required - proceed with normal approval
      console.log('✅ No deposit required, proceeding with approval');
      
      // Store original proposal state for potential rollback
      const originalProposal = proposal;
      
      // **OPTIMISTIC UI UPDATE** - Update state immediately for instant response
      const optimisticProposal = {
        ...proposal,
        status: 'approved' as const,
        notes: JSON.stringify({
          approved_offer_id: offerId,
          approved_at: new Date().toISOString(),
        }),
        services: proposal.services.map(service => {
          if (!service.offer) return service;
          
          return {
            ...service,
            offer: {
              ...service.offer,
              status: service.offer.id === offerId ? 'approved' : 'declined'
            }
          };
        })
      };
      
      // Apply optimistic update after brief delay for smooth UX
      setTimeout(() => {
        setSelectedOffer(offerId);
        setSelectingOffer(null);
        setProposal(optimisticProposal);
      }, 300);
      
      // **DATABASE OPERATION** - Perform actual approval with fallback
      try {
        console.log('🔄 Attempting to approve proposal offer...');
        
        // Try the fast RPC first, fallback to regular function
        let approvalSuccess = false;
        
        try {
          console.log('🚀 Trying fast RPC function...');
          const { error: fastError } = await supabase.rpc('approve_proposal_offer_fast', {
            p_proposal_id: proposal.id,
            p_offer_id: offerId,
            p_business_id: proposal.business_id,
            p_customer_id: proposal.customer_id,
            p_proposal_title: proposal.title,
            p_offer_title: offerServices[0]?.offer?.title || 'Selected Offer'
          });
          
          if (!fastError) {
            console.log('✅ Fast RPC succeeded');
            approvalSuccess = true;
          } else {
            console.warn('⚠️ Fast RPC failed, trying fallback:', fastError);
            throw new Error('Fast RPC failed');
          }
        } catch (fastRpcError) {
          console.log('🔄 Fast RPC failed, using fallback method...');
          
          // Fallback to the regular RPC function
          try {
            const { error: fallbackError } = await supabase.rpc('approve_proposal_offer', { 
              p_offer_id: offerId 
            });
            
            if (!fallbackError) {
              console.log('✅ Fallback RPC succeeded');
              approvalSuccess = true;
            } else {
              console.error('❌ Fallback RPC also failed:', fallbackError);
              throw fallbackError;
            }
          } catch (fallbackRpcError) {
            console.error('❌ Both RPC methods failed:', fallbackRpcError);
            throw fallbackRpcError;
          }
        }
        
        if (approvalSuccess) {
          console.log('✅ Database update completed successfully');

          
          // Keep the optimistic update - don't revert since approval succeeded
          // The UI should now show the thank you page
          
          // Skip background verification to preserve discount data
          // The optimistic update already has all necessary data including discounts
          console.log('🎯 Skipping background verification to preserve discount data in approved proposal');
        }
        
      } catch (rpcError) {
        console.error('❌ All approval methods failed:', rpcError);
        
        // Revert optimistic update only if the database operation completely failed
        setProposal(originalProposal);
        setSelectedOffer(null);
        setSelectingOffer(null);
        
        // Show user-friendly error
        alert('Failed to approve proposal. Please try again or contact support if the issue persists.');
      }
      
    } catch (err) {
      console.error('Error in offer selection:', err);
      setSelectingOffer(null);
      setSelectedOffer(null);
      alert(`Failed to select offer: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  // Open decline confirmation modal
  const handleDeclineAll = () => {
    setShowDeclineModal(true);
  };

  // Actually execute the decline with optional note
  const executeDeclineAll = async () => {
    if (!proposal || !supabase) return;

    try {
      setIsDeclining(true);
      console.log(`Declining proposal ${proposal.id} with note: "${declineNote}"...`);
      
      const declineData = {
        declined_at: new Date().toISOString(),
        declined_by: 'customer',
        ...(declineNote.trim() && { decline_reason: declineNote.trim() })
      };

      const { error } = await supabase
        .from('proposals')
        .update({ 
          status: 'declined',
          notes: JSON.stringify(declineData)
        })
        .eq('id', proposal.id);

      if (error) {
        console.error('Error declining proposal in database:', error);
        throw error;
      }

      console.log('✅ Proposal successfully declined in database');
      
      // Create proposal declined milestone
      try {
        const milestoneData = {
          type: 'custom' as const,
          title: 'Proposal Declined',
          description: `Proposal "${proposal.title}" was declined by customer${declineNote.trim() ? ` - Reason: ${declineNote.trim()}` : ''}`,
          timestamp: new Date().toISOString(),
          metadata: {
            proposal_title: proposal.title,
            entity_type: 'proposal',
            declined_at: new Date().toISOString(),
            declined_by: 'customer',
            ...(declineNote.trim() && { decline_reason: declineNote.trim() })
          }
        };

        await supabase
          .from('booking_milestones')
          .insert([{
            business_id: proposal.business_id,
            customer_id: proposal.customer_id,
            booking_id: proposal.id, // Using proposal ID as booking ID for milestones
            milestone: milestoneData
          }]);

        console.log('✅ Proposal declined milestone created successfully');
      } catch (milestoneError) {
        console.error('❌ Error creating proposal declined milestone:', milestoneError);
        // Continue even if milestone creation fails
      }
      
      // Update local state to trigger DeclinePage render
      setProposal({ 
        ...proposal, 
        status: 'declined',
        notes: JSON.stringify(declineData)
      });
      
      // Close modal
      setShowDeclineModal(false);
      setDeclineNote('');
      
      console.log('✅ Local proposal state updated to declined');
      
    } catch (err) {
      console.error('❌ Error declining proposal:', err);
      alert(`Failed to decline proposal: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsDeclining(false);
    }
  };

  // Prevent unnecessary data refetching after approval
  // The optimistic update should preserve discount data
  useEffect(() => {
    if ((proposal?.status === 'approved' || proposal?.status === 'booked') && supabase) {
      console.log('Approved/booked proposal detected - using existing data to preserve discounts');
    }
  }, [proposal?.id, proposal?.status, supabase]);

  // Handle success callback from Stripe checkout
  useEffect(() => {
    const handleCheckoutSuccess = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const offerId = urlParams.get('offer_id');
      const paymentId = urlParams.get('payment_id');
      const sessionId = urlParams.get('session_id');
      
      // Only process if we have the required params
      if (!offerId || !paymentId || !sessionId) {
        return;
      }
      
      // If supabase or proposal not ready, wait a bit and try again
      if (!supabase) {
        console.log('⏳ Waiting for supabase client...');
        setTimeout(() => handleCheckoutSuccess(), 500);
        return;
      }
      
      console.log('✅ Checkout success detected, verifying payment and approving offer...');
      
      try {
        // First, ensure we have fresh proposal data
        // If proposal not loaded, fetch it directly
        let currentProposal = proposal;
        if (!currentProposal || !currentProposal.id) {
          console.log('⏳ Proposal not loaded yet, fetching...');
          if (!token) {
            console.error('❌ No token available');
            return;
          }
          
          const { data: result, error: rpcError } = await supabase
            .rpc('get_proposal_data_fast', { p_token: token });
          
          if (rpcError || !result?.success || !result.proposal) {
            console.error('❌ Failed to fetch proposal:', rpcError);
            setError('Failed to load proposal data. Please refresh the page.');
            return;
          }
          
          currentProposal = result.proposal;
          setProposal(currentProposal);
          const businessWithLogo1 = {
            ...result.business,
            logo_url: (result.business as any).booking_settings?.logo_url || result.business.logo_url
          };
          setBusiness(businessWithLogo1);
        }
        
        // Verify payment is completed
        const { data: paymentData, error: paymentError } = await supabase
          .from('payments')
          .select('status, stripe_checkout_session_id, proposal_id')
          .eq('id', paymentId)
          .single();
        
        if (paymentError || !paymentData) {
          console.error('❌ Payment not found:', paymentError);
          return;
        }
        
        // Check if offer is already approved or booked (prevent duplicate processing)
        if (currentProposal && (currentProposal.status === 'approved' || currentProposal.status === 'booked')) {
          console.log('✅ Offer already approved/booked, cleaning up URL...');
          window.history.replaceState({}, '', window.location.pathname);
          // Refresh proposal data to show thank you page
          if (token) {
            const { data: result } = await supabase
              .rpc('get_proposal_data_fast', { p_token: token });
            if (result?.success) {
              setProposal(result.proposal);
              const businessWithLogo2 = {
                ...result.business,
                logo_url: (result.business as any).booking_settings?.logo_url || result.business.logo_url
              };
              setBusiness(businessWithLogo2);
              // Set selectedOffer from the refreshed data
              const approvedSvc = result.proposal.services?.find((s: any) => s.offer?.status === 'approved');
              if (approvedSvc?.offer?.id) {
                setSelectedOffer(approvedSvc.offer.id);
              } else if (offerId) {
                setSelectedOffer(offerId);
              }
            }
          }
          return;
        }
        
        // Check if payment is completed (webhook may have already updated it)
        if (paymentData.status === 'completed' || paymentData.stripe_checkout_session_id === sessionId) {
          console.log('✅ Payment verified, approving offer...');
          
          if (!currentProposal || !currentProposal.id) {
            console.error('❌ Cannot find proposal data');
            return;
          }
          
          const offerServices = currentProposal.services.filter((service: any) => 
            service.offer?.id === offerId
          );
          
          // Approve the offer
          const { error: approveError } = await supabase.rpc('approve_proposal_offer_fast', {
            p_proposal_id: currentProposal.id,
            p_offer_id: offerId,
            p_business_id: currentProposal.business_id,
            p_customer_id: currentProposal.customer_id,
            p_proposal_title: currentProposal.title,
            p_offer_title: offerServices[0]?.offer?.title || 'Selected Offer'
          });
          
          if (approveError) {
            console.error('❌ Error approving offer:', approveError);
            // Try fallback
            const { error: fallbackError } = await supabase.rpc('approve_proposal_offer', { p_offer_id: offerId });
            if (fallbackError) {
              console.error('❌ Fallback approval also failed:', fallbackError);
              setError('Failed to approve offer. Please contact support.');
              return;
            }
          }
          
          // Refresh proposal data to get latest state
          if (token) {
            const { data: result } = await supabase
              .rpc('get_proposal_data_fast', { p_token: token });
            if (result?.success) {
              setProposal(result.proposal);
              const businessWithLogo3 = {
                ...result.business,
                logo_url: (result.business as any).booking_settings?.logo_url || result.business.logo_url
              };
              setBusiness(businessWithLogo3);
              setSelectedOffer(offerId);
            }
          }
          
          // Clean up URL parameters after a short delay to ensure state is updated
          setTimeout(() => {
            window.history.replaceState({}, '', window.location.pathname);
          }, 500);
        } else {
          console.log('⏳ Payment not yet completed, waiting for webhook...');
          // Payment might still be processing, webhook will handle it
          // But we should still refresh the proposal data
          setTimeout(async () => {
            if (token) {
              const { data: result } = await supabase
                .rpc('get_proposal_data_fast', { p_token: token });
              if (result?.success) {
                setProposal(result.proposal);
                const businessWithLogo4 = {
                  ...result.business,
                  logo_url: (result.business as any).booking_settings?.logo_url || result.business.logo_url
                };
                setBusiness(businessWithLogo4);
              }
            }
            // Check again after refresh
            handleCheckoutSuccess();
          }, 2000);
        }
      } catch (err) {
        console.error('❌ Error handling checkout success:', err);
        setError('An error occurred processing your payment. Please contact support if the issue persists.');
      }
    };
    
    // Run immediately if we have URL params, don't wait for proposal
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('offer_id') && urlParams.get('payment_id') && urlParams.get('session_id')) {
      handleCheckoutSuccess();
    }
  }, [token, supabase]); // Removed proposal from deps to avoid waiting
  
  useEffect(() => {
    // Only fetch if we have a token and supabase is ready
    if (token && supabase) {
    fetchProposalData();
    } else if (!token) {
      setError('No proposal token provided');
      setIsLoading(false);
    } else if (!supabase) {
      setError('Database connection is not available. Please refresh the page.');
      setIsLoading(false);
    }
  }, [token, supabase]);

  // Function to handle toggle of optional service
  const handleToggleOptionalService = async (serviceId: string) => {
    if (!proposal || !supabase) {
      console.error("Cannot toggle service: supabase client or proposal is null");
      return;
    }
    
    try {
      // Find the service in our local state
      const service = proposal.services.find((s: any) => s.id === serviceId);
      if (!service) {
        console.error(`Service ${serviceId} not found in proposal`);
        return;
      }
      
      // Get the current selection state and toggle it
      const currentlySelected = service.is_selected === true;
      const newSelectionState = !currentlySelected;
      
      console.log(`Toggling service ${serviceId} (${service.name}) from ${currentlySelected} to ${newSelectionState}`);
      
      // Update local state immediately for responsive UI
      setProposal({
        ...proposal,
        services: proposal.services.map((s: any) => 
          s.id === serviceId 
            ? { ...s, is_selected: newSelectionState } 
            : s
        )
      });
      
      // Update the database
      const { error } = await supabase
        .from('proposal_services')
        .update({ is_selected: newSelectionState })
        .eq('id', serviceId);
        
      if (error) {
        console.error(`Error updating service ${serviceId}:`, error);
        
        // Revert local state if there was an error
        setProposal({
          ...proposal,
          services: proposal.services.map((s: any) => 
            s.id === serviceId 
              ? { ...s, is_selected: currentlySelected } 
              : s
          )
        });
        
        alert(`Failed to toggle service: ${error.message}`);
      } else {
        console.log(`Service ${serviceId} successfully updated in database`);
        
        // Verify the update with a direct query
        const { data: verifyDataArray, error: verifyError } = await supabase
          .from('proposal_services')
          .select('is_selected')
          .eq('id', serviceId)
          .limit(1);
        
        const verifyData = verifyDataArray?.[0];
          
        if (verifyError) {
          console.error(`Error verifying service update for ${serviceId}:`, verifyError);
        } else if (verifyData) {
          console.log(`Verified service ${serviceId} is now ${verifyData.is_selected ? 'selected' : 'unselected'}`);
          if (verifyData.is_selected !== newSelectionState) {
            console.warn(`Verification failed: expected ${newSelectionState} but got ${verifyData.is_selected}`);
          }
        }
      }
    } catch (err) {
      console.error('Error toggling service selection:', err);
      alert(`An error occurred: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    const isExpiredOrGone = expired || error.includes('Invalid proposal token') || error.includes('link not found') || error.includes('expired');
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-950 to-black flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-800/60 border border-gray-700/50 flex items-center justify-center">
            {isExpiredOrGone ? (
              <FileText className="w-9 h-9 text-gray-500" />
            ) : (
              <AlertCircle className="w-9 h-9 text-amber-400/80" />
            )}
          </div>
          <h2 className="text-2xl font-semibold text-white mb-3">
            {isExpiredOrGone ? 'Proposal Unavailable' : 'Something Went Wrong'}
          </h2>
          <p className="text-gray-400 text-base leading-relaxed">
            {isExpiredOrGone
              ? 'This proposal has either expired or is no longer available. If you believe this is a mistake, please contact the business that sent you this link.'
              : 'We were unable to load this proposal. Please try again or contact the business that sent you this link.'}
          </p>
          {!isExpiredOrGone && (
            <button
              onClick={() => window.location.reload()}
              className="mt-6 px-6 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!proposal || !business) {
    if (isLoading) {
      return (
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-950 to-black flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-800/60 border border-gray-700/50 flex items-center justify-center">
            <FileText className="w-9 h-9 text-gray-500" />
          </div>
          <h2 className="text-2xl font-semibold text-white mb-3">Proposal Unavailable</h2>
          <p className="text-gray-400 text-base leading-relaxed mb-6">
            This proposal has either expired or is no longer available. If you believe this is a mistake, please contact the business that sent you this link.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Show the decline page if status is declined
  if (proposal.status === 'declined') {
    return <DeclinePage business={business} />;
  }

  // Show thank you page if approved or booked
  if (proposal.status === 'approved' || proposal.status === 'booked') {
    return (
      <ThankYouPage 
        proposal={proposal} 
        business={business} 
        selectedOffer={selectedOffer} 
        supabase={supabase}
      />
    );
  }

  // In the return statement, change how offers are grouped and mapped
  const renderOffers = () => {
    if (!proposal) return null;

    // Group services by offer ID
    const offerGroups: Record<string, Service[]> = {};
    
    proposal.services?.forEach(service => {
      if (service.offer?.id) {
        if (!offerGroups[service.offer.id]) {
          offerGroups[service.offer.id] = [];
        }
        offerGroups[service.offer.id].push(service);
      }
    });

    // Group products by offer ID
    const productGroups: Record<string, Product[]> = {};
    
    proposal.products?.forEach(product => {
      if (product.offer?.id) {
        if (!productGroups[product.offer.id]) {
          productGroups[product.offer.id] = [];
        }
        productGroups[product.offer.id].push(product);
      }
    });

    // Build a short snippet for "partial" view: the first service description,
    // otherwise the list of included service names.
    const buildSnippet = (svcs: Service[]): string => {
      const withDesc = svcs.find((s) => s.description && s.description.trim());
      if (withDesc?.description) return withDesc.description.trim();
      const names = svcs.filter((s) => s.name && !s.isSelectable).map((s) => s.name);
      return names.join(' • ');
    };

    // Convert grouping to array for rendering
    const customerAddress = proposal.customer?.address?.trim() || null;

    const groupedOffers = Object.entries(offerGroups).map(([offerId, services]) => ({
      id: offerId,
      title: services[0]?.offer?.title || "Offer",
      description: buildSnippet(services),
      services: services,
      products: productGroups[offerId] || [],
      showCustomerAddress:
        offerShowCustomerAddress[offerId] === true ||
        services[0]?.offer?.show_customer_address === true,
    }));

    return (
      <div className="space-y-3 sm:space-y-4">
        {groupedOffers.map(offer => (
          <Offer
            key={offer.id}
            title={offer.title}
            description={offer.description}
            partialMode={publicProposalView === 'partial'}
            customerAddress={offer.showCustomerAddress ? customerAddress : null}
            services={offer.services}
            products={offer.products}
            productPhotos={productPhotos}
            servicePhotos={servicePhotos}
            isExpanded={expandedOffers.includes(offer.id)}
            isSelected={selectedOffer === offer.id}
            isSelecting={selectingOffer === offer.id}
            onToggle={() => handleToggleOffer(offer.id)}
            onSelect={() => handleSelectOffer(offer.id)}
            depositPercentage={offerDeposits[offer.id] || null}
            businessTaxRates={publicBusinessTaxRates}
            serviceAdditionalTaxes={publicServiceAddlTaxes}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen text-blue-100 relative overflow-hidden" style={{
      background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.9) 70%, rgba(0,0,0,1) 100%)'
    }}>
      {/* Subtle animated overlay */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-green-500/5" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/30 to-transparent" />
      </div>

      {/* Business Header */}
      <div className="relative">
        <div className="bg-black/40 backdrop-blur-xl border-b border-white/10">
          <div className="relative max-w-4xl mx-auto px-3 sm:px-6 py-3 sm:py-4">
            <div className="flex flex-col items-center">
              {(() => {
                const logoSizeClass =
                  logoSize === 'small'
                    ? 'h-14 sm:h-16 max-h-14 sm:max-h-16'
                    : logoSize === 'large'
                      ? 'h-28 sm:h-36 max-h-28 sm:max-h-36'
                      : 'h-20 sm:h-24 max-h-20 sm:max-h-24';
                const showLogo = !!business?.logo_url;
                const showName = !logoOnly || !showLogo;
                return (
                  <div className={`flex items-center justify-center ${showLogo && showName ? 'gap-2.5 sm:gap-3' : ''}`}>
                    {showLogo && (
                      <img
                        src={business.logo_url}
                        alt={business.name || 'Business logo'}
                        className={`${logoSizeClass} w-auto object-contain flex-shrink-0`}
                      />
                    )}
                    {showName && (
                      <div>
                        <h1 className="text-base sm:text-xl font-bold text-white font-orbitron">{business?.name || 'Business Name'}</h1>
                      </div>
                    )}
                  </div>
                );
              })()}
              {googleReviewsBadge.enabled && googleReviewsBadge.rating != null && (() => {
                const rating = Math.min(5, Math.max(0, googleReviewsBadge.rating));
                const fullStars = Math.round(rating);
                const badgeInner = (
                  <span className="inline-flex items-center gap-1">
                    <GoogleGIcon size={12} />
                    <span className="text-amber-400 text-[11px] sm:text-[12px] leading-none tracking-tight" aria-hidden="true">
                      {'★'.repeat(fullStars)}
                      <span className="text-amber-400/35">{'★'.repeat(5 - fullStars)}</span>
                    </span>
                    <span className="text-[11px] sm:text-[12px] font-semibold text-white">{rating}</span>
                  </span>
                );
                return (
                  <div className="mt-0.5">
                    {googleReviewsBadge.url ? (
                      <a
                        href={googleReviewsBadge.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center opacity-90 hover:opacity-100 transition-opacity touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                        aria-label={`Rated ${rating} out of 5 on Google — read reviews`}
                      >
                        {badgeInner}
                      </a>
                    ) : (
                      <span className="inline-flex items-center" aria-label={`Rated ${rating} out of 5 on Google`}>
                        {badgeInner}
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative max-w-4xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
        <div className="mb-4 sm:mb-8">
          <div className="text-center bg-white/5 backdrop-blur-xl py-2.5 sm:py-4 px-3 sm:px-6 rounded-xl sm:rounded-2xl border border-white/10">
            <p className="text-blue-100 text-xs sm:text-base">
              Select 1 of the {Object.keys(proposal.services?.reduce((acc: Record<string, boolean>, service: any) => {
                if (service.offer?.id) acc[service.offer.id] = true;
                return acc;
              }, {} as Record<string, boolean>) || {}).length} offers you would like to move forward with below
            </p>
            {(proposal.created_at || proposal.valid_until) && (
              <p className="mt-1.5 sm:mt-2 text-[10px] sm:text-xs text-blue-200/40">
                {[
                  proposal.created_at
                    ? `Quote date ${new Date(proposal.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}`
                    : null,
                  proposal.valid_until
                    ? `Valid until ${new Date(proposal.valid_until).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}`
                    : null,
                ].filter(Boolean).join('  ·  ')}
              </p>
            )}
          </div>
        </div>

        {renderOffers()}

        {(proposal.status === 'sent' || proposal.status === 'viewed') && (
          <div className="text-center mt-6 sm:mt-12">
            <p className="text-xs sm:text-sm text-blue-200/70 mb-3 sm:mb-6 bg-white/5 backdrop-blur-xl py-2.5 sm:py-4 px-3 sm:px-6 rounded-xl sm:rounded-2xl border border-white/10">
              If you choose not to select any of the offers above, please click 'Decline All' below.
            </p>
            <button
              onClick={handleDeclineAll}
              className="
                inline-flex items-center px-4 sm:px-6 py-1.5 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium
                bg-red-500/20 text-red-400 border border-red-500/30
                hover:bg-red-500/30 transition-all duration-300
                font-orbitron
              "
            >
              <X className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
              DECLINE ALL
            </button>
          </div>
        )}

        <div className="text-center mt-6 sm:mt-12 text-xs sm:text-sm text-blue-200/70 bg-white/5 backdrop-blur-xl py-2.5 sm:py-4 px-3 sm:px-6 rounded-xl sm:rounded-2xl border border-white/10">
          <p className="space-y-1 sm:space-y-0">
            <span className="block sm:inline">If you have any questions, contact {business.name}</span>
            <span className="block sm:inline sm:ml-1">
              at <a href={`mailto:${business.email}`} className="text-blue-400 hover:text-blue-300 transition-colors">{business.email}</a>
              {' '}or call at{' '}
              <a href={`tel:${business.phone}`} className="text-blue-400 hover:text-blue-300 transition-colors">{business.phone}</a>
            </span>
          </p>
        </div>

        {customPublicMessage && (
          <div className="mt-4 sm:mt-6 bg-white/5 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-white/10 p-4 sm:p-6">
            <p className="text-sm sm:text-base text-blue-100 whitespace-pre-line text-center">
              {customPublicMessage}
            </p>
          </div>
        )}
      </div>

      {/* Decline Confirmation Modal */}
      {showDeclineModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowDeclineModal(false)}
          />
          
          {/* Modal */}
          <div className="relative bg-black/90 backdrop-blur-xl border border-white/20 rounded-2xl p-6 sm:p-8 mx-4 max-w-md w-full">
            <div className="text-center mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-500/20 mb-4">
                <X className="h-6 w-6 text-red-400" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-white font-orbitron mb-2">
                Decline All Offers
              </h3>
              <p className="text-sm text-blue-200/70">
                Are you sure you want to decline all offers? This action cannot be undone.
              </p>
            </div>

            {/* Optional Note Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-blue-200 mb-2">
                Reason for declining (optional)
              </label>
              <textarea
                value={declineNote}
                onChange={(e) => setDeclineNote(e.target.value)}
                placeholder="Let us know why you're declining..."
                className="
                  w-full px-3 py-3 rounded-xl
                  bg-white/5 border border-white/20
                  text-white placeholder-blue-300/50
                  focus:border-blue-400/50 focus:outline-none focus:ring-2 focus:ring-blue-400/20
                  resize-none
                "
                rows={3}
                maxLength={500}
              />
              <p className="text-xs text-blue-300/50 mt-1">
                {declineNote.length}/500 characters
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeclineModal(false)}
                className="
                  flex-1 px-4 py-2.5 rounded-xl text-sm font-medium
                  bg-white/10 text-blue-200 border border-white/20
                  hover:bg-white/20 transition-all duration-300
                  font-orbitron
                "
              >
                Cancel
              </button>
              <button
                onClick={executeDeclineAll}
                disabled={isDeclining}
                className="
                  flex-1 px-4 py-2.5 rounded-xl text-sm font-medium
                  bg-red-500/20 text-red-400 border border-red-500/30
                  hover:bg-red-500/30 transition-all duration-300
                  disabled:opacity-50 disabled:cursor-not-allowed
                  font-orbitron flex items-center justify-center
                "
              >
                {isDeclining ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-400 border-t-transparent mr-2" />
                    Declining...
                  </>
                ) : (
                  'DECLINE ALL'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deposit Payment Modal */}
      {showDepositPayment && depositPaymentData && depositStripePromise && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="fixed inset-0 bg-black/70 backdrop-blur-sm"
          />
          <div className="relative bg-white rounded-2xl shadow-2xl mx-4 max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 sm:p-8">
            <button
              onClick={() => {
                if (depositPaymentData) {
                  supabase
                    .from('payments')
                    .update({ status: 'cancelled' })
                    .eq('id', depositPaymentData.paymentId)
                    .then(() => {});
                }
                setShowDepositPayment(false);
                setDepositPaymentData(null);
                setDepositStripePromise(null);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="text-center mb-6">
              <CreditCard className="w-12 h-12 text-blue-600 mx-auto mb-3" />
              <h3 className="text-xl font-semibold text-gray-900 mb-1">Pay Deposit</h3>
              <p className="text-sm text-gray-500">
                {depositPaymentData.depositPercentage}% deposit for {depositPaymentData.offerTitle}
              </p>
            </div>
            <Elements stripe={depositStripePromise} options={{ appearance: { theme: 'stripe' as const } }}>
              <ProposalDepositPaymentForm
                depositData={depositPaymentData}
                onSuccess={async () => {
                  if (!proposal || !depositPaymentData) return;

                  const offerId = depositPaymentData.offerId;
                  const offerServices = proposal.services.filter(s => s.offer?.id === offerId);

                  const { error: approveError } = await supabase.rpc('approve_proposal_offer_fast', {
                    p_proposal_id: proposal.id,
                    p_offer_id: offerId,
                    p_business_id: proposal.business_id,
                    p_customer_id: proposal.customer_id,
                    p_proposal_title: proposal.title,
                    p_offer_title: offerServices[0]?.offer?.title || depositPaymentData.offerTitle
                  });

                  if (approveError) {
                    console.error('❌ Error approving offer:', approveError);
                    await supabase.rpc('approve_proposal_offer', { p_offer_id: offerId });
                  }

                  if (token) {
                    const { data: result } = await supabase.rpc('get_proposal_data_fast', { p_token: token });
                    if (result?.success) {
                      setProposal(result.proposal);
                      const businessWithLogo = {
                        ...result.business,
                        logo_url: (result.business as any).booking_settings?.logo_url || result.business.logo_url
                      };
                      setBusiness(businessWithLogo);
                      setSelectedOffer(offerId);
                    }
                  }

                  setShowDepositPayment(false);
                  setDepositPaymentData(null);
                  setDepositStripePromise(null);
                }}
                onCancel={() => {
                  if (depositPaymentData) {
                    supabase
                      .from('payments')
                      .update({ status: 'cancelled' })
                      .eq('id', depositPaymentData.paymentId)
                      .then(() => {});
                  }
                  setShowDepositPayment(false);
                  setDepositPaymentData(null);
                  setDepositStripePromise(null);
                }}
              />
            </Elements>
          </div>
        </div>
      )}
    </div>
  );
}

export default PublicProposal;


